import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { getProducts, getProductIntegration, getOrganizationsForIntegration, getMultipleIntegrationsStatus, updateProductIntegrationStatus, getIntegrationValues, createProductIntegration, deleteProductIntegration, getLoggerCustomer, getLoggerCustomers, generateConfigFile, updateLoggerCustomerStatus, getLoggerIntegrationsBatch } from '../../services/api';
import { Product, Tenant, LoggerProduct } from '../../types';
import { formatDate } from '../../utils/format';
import { downloadConfigFile } from '../../utils/ConfigFile.js';
import { usePermissions } from '../../hooks/usePermissions';
import { 
  CheckCircle, 
  AlertTriangle, 
  ExternalLink, 
  Boxes, 
  Loader2, 
  Building2, 
  ChevronDown, 
  ChevronRight, 
  Users, 
  Filter,
  Search,
  PieChart,
  BarChart3,
  ListFilter,
  Layers,
  X,
  Calendar,
  Save,
  LayoutGrid,
  List,
  Check,
  Trash2,
  Download,
  SlidersHorizontal,
  Eye,
  Plus
} from 'lucide-react';
import CustomerSelector from './CustomerSelector';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import ContentLoader from '../../components/ui/ContentLoader';
import LoggerConfigModal from '../../components/logger/LoggerConfigModal';
import { createPortal } from 'react-dom';

// Define a proper type for batch API response
interface IntegrationBatchResponse {
  [key: string]: any; // Allow any string keys to avoid TypeScript indexing errors
  active_status?: {
    [productId: string]: {
      [orgId: string]: boolean;
    };
  };
}

// Update IntegrationField type to match the one in the main types file
interface IntegrationField {
  field_key: string;
  label: string;
  type: 'text' | 'number' | 'secret' | 'url' | 'multi-select' | 'soar-mapping';
  is_required: boolean;
  field_type?: string;
  description?: string;
  options?: string[]; // For multi-select fields
}

// Update Product type to use our enhanced IntegrationField
interface EnhancedProduct extends Omit<Product, 'integration_fields'> {
  integration_fields: IntegrationField[];
}

// Integration Config Modal Component
interface ConfigModalProps {
  product: EnhancedProduct;
  organization: Tenant;
  isOpen: boolean;
  onClose: () => void;
  onSave: (productId: string, orgId: string, values: Record<string, string | string[]>) => void;
}

// View Config Modal Component
interface ViewConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  configContent: string;
  orgName: string;
}

// Define the download modal props interface
interface DownloadConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  products: LoggerProduct[];
  selectedProducts: string[];
  onProductToggle: (productId: string) => void;
  onDownload: () => void;
  isDownloading: boolean;
}

// Download Configuration Modal component
const DownloadConfigModal: React.FC<DownloadConfigModalProps> = ({
  isOpen,
  onClose,
  products,
  selectedProducts,
  onProductToggle,
  onDownload,
  isDownloading
}) => {
  if (!isOpen) return null;
  
  return createPortal(
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[200] flex items-center justify-center p-4"
      onClick={() => onClose()}
    >
      <div 
        className="bg-background-card rounded-lg shadow-xl w-full max-w-lg animate-modalOpen"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-border-dark">
          <h3 className="text-lg font-semibold">Download Configuration</h3>
          <button 
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="p-1 rounded-full hover:bg-background-hover text-gray-400 hover:text-gray-200"
          >
            <X size={20} />
          </button>
        </div>
        
        <div className="p-5">
          <p className="text-sm text-gray-300 mb-4">
            Select the products you want to include in the configuration file:
          </p>
          
          <div className="space-y-2 max-h-60 overflow-y-auto mb-4">
            {products.map(product => (
              <div key={product.id} className="flex items-center">
                <input
                  type="checkbox"
                  id={`download-${product.id}`}
                  checked={selectedProducts.includes(product.id || '')}
                  onChange={(e) => {
                    e.stopPropagation();
                    if (product.id) {
                      onProductToggle(product.id);
                    }
                  }}
                  className="mr-2 h-4 w-4 rounded border-gray-600 bg-gray-800 text-primary"
                />
                <label 
                  htmlFor={`download-${product.id}`} 
                  className="text-sm text-gray-300"
                  onClick={(e) => e.stopPropagation()}
                >
                  {product.product_name || 'Unknown Product'}
                </label>
              </div>
            ))}
          </div>
          
          <div className="flex justify-between mt-4">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              className="px-4 py-2 border border-gray-600 text-gray-300 rounded-md hover:bg-gray-700"
            >
              Cancel
            </button>
            
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDownload();
              }}
              disabled={isDownloading || selectedProducts.length === 0}
              className={`flex items-center px-4 py-2 rounded-md text-white ${
                selectedProducts.length > 0 ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-600 cursor-not-allowed'
              }`}
            >
              {isDownloading ? (
                <Loader2 size={16} className="animate-spin mr-2" />
              ) : (
                <Download size={16} className="mr-2" />
              )}
              Download Selected ({selectedProducts.length})
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

const ViewConfigModal: React.FC<ViewConfigModalProps> = ({ 
  isOpen, 
  onClose, 
  configContent, 
  orgName 
}) => {
  if (!isOpen) return null;
  
  return (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4 overflow-hidden" 
      onClick={onClose}
    >
      <div 
        className="bg-background-card rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto animate-modalOpen"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border-dark sticky top-0 bg-background-card z-10">
          <div>
            <h2 className="text-xl font-semibold">Logger Configuration</h2>
            <div className="text-sm text-text-muted-dark">
              {orgName}
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 rounded-full hover:bg-background-hover text-gray-400 hover:text-gray-200 transition-colors"
            aria-label="Close"
          >
            <X size={24} />
          </button>
        </div>

        {/* Config content */}
        <div className="p-4">
          <div className="bg-background-darker rounded-md p-4 overflow-x-auto">
            <pre className="text-sm text-gray-300 whitespace-pre-wrap font-mono">
              {configContent}
            </pre>
          </div>
        </div>

        {/* Actions */}
        <div className="p-4 border-t border-border-dark flex justify-end gap-3">
          <button
            onClick={onClose}
            className="premium-button-outline"
          >
            Close
          </button>
          <button
            onClick={() => {
              downloadConfigFile(
                configContent,
                `${orgName.replace(/\s+/g, '_').toLowerCase()}_rsyslog.conf`
              );
            }}
            className="premium-button-primary flex items-center"
          >
            <Download size={16} className="mr-2" />
            Download
          </button>
        </div>
      </div>
    </div>
  );
};

const IntegrationConfigModal: React.FC<ConfigModalProps> = ({ 
  product, 
  organization, 
  isOpen, 
  onClose, 
  onSave 
}) => {
  const [accounts, setAccounts] = useState<Array<Record<string, string | string[]>>>([{}]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDropdownField, setOpenDropdownField] = useState<Record<string, string | null>>({});
  const [customOptions, setCustomOptions] = useState<Record<string, string>>({});
  
  // Initialize form values with any existing data
  useEffect(() => {
    const fetchExistingValues = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const productId = product.id || product._id as string;
        const orgId = organization.id;
        
        // Initialize empty account structure
        const initializeAccount = (): Record<string, string | string[]> => {
          const accountValues: Record<string, string | string[]> = {};
          product.integration_fields.forEach(field => {
            if (field.type !== 'soar-mapping') {
              if (field.type === 'multi-select') {
                accountValues[field.field_key] = [];
              } else {
                accountValues[field.field_key] = '';
              }
            }
          });
          return accountValues;
        };
        
        // Try to fetch existing values if this is an update
        try {
          const existingValues = await getIntegrationValues(productId, orgId);
          
          if (existingValues && existingValues.values) {
            // Check if existing values contain accounts array
            if (existingValues.values.accounts && Array.isArray(existingValues.values.accounts)) {
              // Multiple accounts exist
              const loadedAccounts = existingValues.values.accounts.map((account: any) => {
                const accountValues = initializeAccount();
                Object.entries(account).forEach(([key, value]) => {
                  const isMultiSelect = product.integration_fields.some(
                    field => field.field_key === key && field.type === 'multi-select'
                  );
                  
                  if (isMultiSelect) {
                    if (typeof value === 'string') {
                      accountValues[key] = value.split(',').map((v: string) => v.trim()).filter(Boolean);
                    } else if (Array.isArray(value)) {
                      accountValues[key] = value;
                    } else {
                      accountValues[key] = [];
                    }
                  } else {
                    accountValues[key] = value as string;
                  }
                });
                return accountValues;
              });
              setAccounts(loadedAccounts.length > 0 ? loadedAccounts : [initializeAccount()]);
            } else {
              // Single account (legacy format) - convert to accounts array
              const accountValues = initializeAccount();
              Object.entries(existingValues.values).forEach(([key, value]) => {
                if (key !== 'accounts') {
                  const isMultiSelect = product.integration_fields.some(
                    field => field.field_key === key && field.type === 'multi-select'
                  );
                  
                  if (isMultiSelect) {
                    if (typeof value === 'string') {
                      accountValues[key] = value.split(',').map((v: string) => v.trim()).filter(Boolean);
                    } else if (Array.isArray(value)) {
                      accountValues[key] = value;
                    } else {
                      accountValues[key] = [];
                    }
                  } else {
                    accountValues[key] = value as string;
                  }
                }
              });
              setAccounts([accountValues]);
            }
          } else {
            // No existing values - start with one empty account
            setAccounts([initializeAccount()]);
          }
        } catch (error) {
          // Continue with empty account if fetch fails
          setAccounts([initializeAccount()]);
        }
      } catch (err) {
        console.error('Error fetching integration data:', err);
        setError('Failed to load existing integration data');
        // Initialize with empty account on error
        const emptyAccount: Record<string, string | string[]> = {};
        product.integration_fields.forEach(field => {
          if (field.type !== 'soar-mapping') {
            if (field.type === 'multi-select') {
              emptyAccount[field.field_key] = [];
            } else {
              emptyAccount[field.field_key] = '';
            }
          }
        });
        setAccounts([emptyAccount]);
      } finally {
        setIsLoading(false);
      }
    };
    
    if (isOpen) {
      fetchExistingValues();
    }
  }, [product, organization, isOpen]);
  
  // Add a new account configuration
  const handleAddAccount = () => {
    const emptyAccount: Record<string, string | string[]> = {};
    product.integration_fields.forEach(field => {
      if (field.type !== 'soar-mapping') {
        if (field.type === 'multi-select') {
          emptyAccount[field.field_key] = [];
        } else {
          emptyAccount[field.field_key] = '';
        }
      }
    });
    setAccounts(prev => [...prev, emptyAccount]);
  };

  // Remove an account configuration
  const handleRemoveAccount = (index: number) => {
    if (accounts.length > 1) {
      setAccounts(prev => prev.filter((_, i) => i !== index));
    }
  };

  // Handle input changes for regular fields
  const handleInputChange = (accountIndex: number, key: string, value: string) => {
    setAccounts(prev => {
      const updated = [...prev];
      updated[accountIndex] = {
        ...updated[accountIndex],
        [key]: value
      };
      return updated;
    });
  };
  
  // Handle dropdown toggle for multi-select fields
  const handleDropdownToggle = (accountIndex: number, fieldKey: string) => {
    const dropdownKey = `${accountIndex}-${fieldKey}`;
    setOpenDropdownField(prev => ({
      ...prev,
      [dropdownKey]: prev[dropdownKey] === dropdownKey ? null : dropdownKey
    }));
  };
  
  // Handle option selection in multi-select
  const handleOptionSelect = (accountIndex: number, fieldKey: string, option: string) => {
    setAccounts(prev => {
      const updated = [...prev];
      const currentValues = Array.isArray(updated[accountIndex][fieldKey]) 
        ? updated[accountIndex][fieldKey] as string[]
        : [];
      
      if (currentValues.includes(option)) {
        updated[accountIndex] = {
          ...updated[accountIndex],
          [fieldKey]: currentValues.filter((item: string) => item !== option)
        };
      } else {
        updated[accountIndex] = {
          ...updated[accountIndex],
          [fieldKey]: [...currentValues, option]
        };
      }
      return updated;
    });
  };
  
  // Remove a selected option
  const handleRemoveOption = (accountIndex: number, fieldKey: string, option: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setAccounts(prev => {
      const updated = [...prev];
      const currentValues = Array.isArray(updated[accountIndex][fieldKey]) 
        ? updated[accountIndex][fieldKey] as string[]
        : [];
        
      updated[accountIndex] = {
        ...updated[accountIndex],
        [fieldKey]: currentValues.filter((item: string) => item !== option)
      };
      return updated;
    });
  };

  // Add a custom option to multi-select field
  const handleAddCustomOption = (accountIndex: number, fieldKey: string) => {
    const customOptionKey = `${accountIndex}-${fieldKey}`;
    const customOption = customOptions[customOptionKey]?.trim();
    if (!customOption) return;

    setAccounts(prev => {
      const updated = [...prev];
      const currentValues = Array.isArray(updated[accountIndex][fieldKey]) 
        ? updated[accountIndex][fieldKey] as string[]
        : [];
      
      if (!currentValues.includes(customOption)) {
        updated[accountIndex] = {
          ...updated[accountIndex],
          [fieldKey]: [...currentValues, customOption]
        };
      }
      return updated;
    });

    // Clear the custom option input
    setCustomOptions(prev => ({ ...prev, [customOptionKey]: '' }));
  };

  // Handle custom option input change
  const handleCustomOptionChange = (accountIndex: number, fieldKey: string, value: string) => {
    const customOptionKey = `${accountIndex}-${fieldKey}`;
    setCustomOptions(prev => ({ ...prev, [customOptionKey]: value }));
  };
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (target && !target.closest('.multi-select-container')) {
        setOpenDropdownField({});
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Prepare values for submission - store accounts as array if multiple, or single object if one
    const valuesToSave = accounts.length === 1 
      ? accounts[0]  // Single account - save as flat structure (backward compatible)
      : { accounts };  // Multiple accounts - save as accounts array
    
    setTimeout(() => {
      onSave(product.id as string, organization.id, valuesToSave as any);
      setIsSubmitting(false);
      onClose();
    }, 800);
  };
  
  if (!isOpen) return null;
  
  const productId = product.id || product._id as string;
  
  return (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4 overflow-hidden" 
      onClick={onClose}
    >
      <div 
        className="bg-background-card rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto animate-modalOpen"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Product header */}
        <div className="flex items-center justify-between p-4 border-b border-border-dark sticky top-0 bg-background-card z-10">
          <div className="flex items-center">
            {product.logo_url ? (
              <img 
                src={product.logo_url}
                alt={product.name}
                className="h-12 w-12 mr-3 object-contain bg-white rounded p-1"
              />
            ) : (
              <div className="h-12 w-12 mr-3 flex items-center justify-center bg-background-hover rounded">
                <span className="text-lg font-bold text-text-muted-dark">
                  {product.name.substring(0, 2).toUpperCase()}
                </span>
              </div>
            )}
            <div>
              <h2 className="text-xl font-semibold">{product.name}</h2>
              <div className="flex items-center text-sm text-text-muted-dark">
                Integration Configuration for {organization.name}
              </div>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 rounded-full hover:bg-background-hover text-gray-400 hover:text-gray-200 transition-colors"
            aria-label="Close"
          >
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        {isLoading ? (
          <div className="p-6">
            <ContentLoader
              message="Loading integration data..."
              size="lg"
              withCard={false}
              minHeight="300px"
            />
          </div>
        ) : error ? (
          <div className="p-6 text-center">
            <AlertTriangle size={24} className="mx-auto mb-2 text-status-warning" />
            <p className="text-status-warning">{error}</p>
            <button 
              onClick={onClose}
              className="mt-4 premium-button-outline"
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6">
            {/* Accounts Section */}
            <div className="space-y-6">
              {accounts.map((account, accountIndex) => (
                <div key={accountIndex} className="border border-border-dark rounded-lg p-5 bg-background-hover/30">
                  {/* Account Header */}
                  <div className="flex items-center justify-between mb-4 pb-3 border-b border-border-dark">
                    <h3 className="text-lg font-medium">
                      Account {accountIndex + 1}
                    </h3>
                    {accounts.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveAccount(accountIndex)}
                        className="p-1.5 rounded-full hover:bg-status-danger/20 text-status-danger hover:text-status-danger transition-colors"
                        aria-label="Remove account"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                  
                  {/* Fields for this account */}
                  <div className="space-y-4">
                    {product.integration_fields
                      .filter(field => field.type !== 'soar-mapping')
                      .map(field => {
                        const dropdownKey = `${accountIndex}-${field.field_key}`;
                        const isDropdownOpen = openDropdownField[dropdownKey] === dropdownKey;
                        const customOptionKey = `${accountIndex}-${field.field_key}`;
                        
                        return (
                          <div key={field.field_key} className="form-group">
                            <label className="form-label">
                              {field.label}
                              {field.is_required && <span className="text-status-danger ml-1">*</span>}
                            </label>
                            
                            {field.type === 'multi-select' ? (
                              <div className="multi-select-container relative">
                                {/* Selected values display area */}
                                <div 
                                  className="w-full bg-background-hover border border-border-dark rounded-md px-3 py-2 min-h-[40px] cursor-pointer"
                                  onClick={() => handleDropdownToggle(accountIndex, field.field_key)}
                                >
                                  <div className="flex flex-wrap gap-2 items-center">
                                    {Array.isArray(account[field.field_key]) && (account[field.field_key] as string[]).length > 0 ? (
                                      <>
                                        {(account[field.field_key] as string[]).map((value: string) => (
                                          <div key={value} className="bg-primary-700/30 text-primary-300 px-2 py-0.5 rounded text-xs flex items-center">
                                            <span>{value}</span>
                                            <button 
                                              type="button"
                                              onClick={(e) => handleRemoveOption(accountIndex, field.field_key, value, e)}
                                              className="ml-1 text-primary-300/70 hover:text-primary-300"
                                            >
                                              <X size={12} />
                                            </button>
                                          </div>
                                        ))}
                                      </>
                                    ) : (
                                      <span className="text-gray-400 text-sm">
                                        Select {field.label.toLowerCase()} or add custom options...
                                      </span>
                                    )}
                                    <div className="ml-auto">
                                      <ChevronDown 
                                        size={16} 
                                        className={`transition-transform duration-200 ${
                                          isDropdownOpen ? 'rotate-180' : ''
                                        }`} 
                                      />
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Dropdown options */}
                                {isDropdownOpen && (
                                  <div className="absolute z-50 mt-1 w-full bg-gray-800 border border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                                    {/* Custom option input */}
                                    <div className="p-3 border-b border-gray-600">
                                      <div className="flex items-center gap-2">
                                        <input
                                          type="text"
                                          placeholder="Add custom option..."
                                          value={customOptions[customOptionKey] || ''}
                                          onChange={(e) => handleCustomOptionChange(accountIndex, field.field_key, e.target.value)}
                                          className="flex-1 bg-gray-700 border border-gray-600 rounded px-2 py-1 text-sm text-white placeholder-gray-400"
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                              e.preventDefault();
                                              handleAddCustomOption(accountIndex, field.field_key);
                                            }
                                          }}
                                        />
                                        <button
                                          type="button"
                                          onClick={() => handleAddCustomOption(accountIndex, field.field_key)}
                                          className="px-2 py-1 bg-primary-600 hover:bg-primary-700 text-white text-xs rounded transition-colors"
                                        >
                                          Add
                                        </button>
                                      </div>
                                    </div>
                                    
                                    {/* Predefined options */}
                                    {field.options && field.options.length > 0 ? (
                                      field.options.map((option: string) => {
                                        const isSelected = Array.isArray(account[field.field_key]) && 
                                          (account[field.field_key] as string[]).includes(option);
                                        return (
                                          <div
                                            key={option}
                                            className={`px-3 py-2 cursor-pointer hover:bg-gray-700 ${
                                              isSelected
                                                ? 'bg-primary-700/50 text-primary-200' 
                                                : 'text-white'
                                            }`}
                                            onClick={() => handleOptionSelect(accountIndex, field.field_key, option)}
                                          >
                                            <div className="flex items-center justify-between">
                                              <span>{option}</span>
                                              {isSelected && (
                                                <Check size={16} className="text-primary-300" />
                                              )}
                                            </div>
                                          </div>
                                        );
                                      })
                                    ) : (
                                      <div className="px-3 py-2 text-gray-300 text-center">
                                        No predefined options available
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <input
                                type={field.field_type === 'password' ? 'password' : 'text'}
                                value={typeof account[field.field_key] === 'string' ? account[field.field_key] as string : ''}
                                onChange={(e) => handleInputChange(accountIndex, field.field_key, e.target.value)}
                                placeholder={field.description || `Enter ${field.label}`}
                                className="form-input"
                                required={field.is_required}
                              />
                            )}
                            
                            {field.description && (
                              <p className="text-xs text-text-muted-dark mt-1">
                                {field.description}
                              </p>
                            )}
                          </div>
                        );
                      })}
                  </div>
                </div>
              ))}
              
              {/* Add Account Button */}
              <div className="flex justify-center pt-2">
                <button
                  type="button"
                  onClick={handleAddAccount}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary-300 hover:text-primary-200 bg-primary-700/20 hover:bg-primary-700/30 border border-primary-700/50 rounded-lg transition-colors"
                >
                  <Plus size={16} />
                  Add Another Account
                </button>
              </div>
            </div>

            {/* Actions */}
            <div className="mt-6 pt-4 border-t border-border-dark flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="premium-button-outline"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="premium-button-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <span className="flex items-center">
                    <Loader2 size={16} className="animate-spin mr-2" />
                    Saving...
                  </span>
                ) : (
                  <span className="flex items-center">
                    <Save size={16} className="mr-2" />
                    Save
                  </span>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

const IntegrationsPage: React.FC = () => {
  const { canManageIntegrations } = usePermissions();
  const [products, setProducts] = useState<EnhancedProduct[]>([]);
  const [organizations, setOrganizations] = useState<Tenant[]>([]);
  const [selectedOrgs, setSelectedOrgs] = useState<Tenant[]>([]);
  const [expandedProductId, setExpandedProductId] = useState<string | null>(null);
  const [integrationStatus, setIntegrationStatus] = useState<Record<string, Record<string, boolean>>>({});
  const [activationStatus, setActivationStatus] = useState<Record<string, Record<string, boolean>>>({});
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isContentLoading, setIsContentLoading] = useState(false);
  const hasLoadedOnceRef = useRef(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [activeView, setActiveView] = useState<'products' | 'stats' | 'logger'>('products');
  const [loadingAdditionalData, setLoadingAdditionalData] = useState(false);
  const [dataFullyLoaded, setDataFullyLoaded] = useState(false);
  const [selectedLevels, setSelectedLevels] = useState<string[]>([]);
  const [integrationDates, setIntegrationDates] = useState<Record<string, Record<string, string>>>({});
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [selectedOrgForConfig, setSelectedOrgForConfig] = useState<Tenant | null>(null);
  const [selectedProductForConfig, setSelectedProductForConfig] = useState<EnhancedProduct | null>(null);
  const [clientViewMode, setClientViewMode] = useState<'grid' | 'table'>('grid');
  // Track which toggles are currently being updated
  const [loadingToggles, setLoadingToggles] = useState<Record<string, boolean>>({});
  
  // New state for logger config modal
  const [loggerConfigModalOpen, setLoggerConfigModalOpen] = useState(false);
  const [selectedOrgForLoggerConfig, setSelectedOrgForLoggerConfig] = useState<Tenant | null>(null);
  const [selectedProductForLoggerConfig, setSelectedProductForLoggerConfig] = useState<Product | null>(null);
  // Track logger integrations
  const [loggerIntegrations, setLoggerIntegrations] = useState<Record<string, string[]>>({});
  // Filter for product type
  const [productTypeFilter, setProductTypeFilter] = useState<'all' | 'saas' | 'logger'>('all');
  
  // Delete confirmation modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteInfo, setDeleteInfo] = useState<{productId: string, orgId: string, orgName: string} | null>(null);
  
  // View config modal state
  const [viewConfigModalOpen, setViewConfigModalOpen] = useState(false);
  const [configContent, setConfigContent] = useState<string>('');
  const [configOrgName, setConfigOrgName] = useState<string>('');
  const [isLoadingConfig, setIsLoadingConfig] = useState<Record<string, boolean>>({});
  
  // Download config modal state
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [productsForDownload, setProductsForDownload] = useState<string[]>([]);
  const [selectedOrgForDownload, setSelectedOrgForDownload] = useState<Tenant | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  
  // Get the available organization levels
  const availableLevels = ['L0', 'L2', 'L3'];
  
  // Initialize levels to show based on the query string
  useEffect(() => {
    // Default to showing only L3 organizations
    setSelectedLevels(['L3']);
  }, []);

  // Set the default view based on saved preference or default to 'products'
  useEffect(() => {
    const savedView = localStorage.getItem('integration-view');
    if (savedView === 'stats' || savedView === 'products' || savedView === 'logger') {
      setActiveView(savedView);
      
      // Set product type filter based on the active view
      if (savedView === 'logger') {
        setProductTypeFilter('logger');
      }
    }
    
    // Always default to grid view
    setClientViewMode('grid');
    // Save this preference to localStorage
    localStorage.setItem('client-view-mode', 'grid');
  }, []);
  
  // Sync product type filter only when switching tabs (not when user picks a filter on Products view)
  const prevActiveViewRef = useRef(activeView);
  useEffect(() => {
    const previousView = prevActiveViewRef.current;
    prevActiveViewRef.current = activeView;

    if (activeView === 'logger') {
      setProductTypeFilter('logger');
    } else if (previousView === 'logger' && activeView === 'products') {
      setProductTypeFilter('all');
    }
  }, [activeView]);
  
  // Clear search term when switching between tabs
  useEffect(() => {
    setSearchTerm('');
  }, [activeView]);
  
  // Debug client view mode changes
  useEffect(() => {
            // Client view mode updated
  }, [clientViewMode]);
  
  // Load products and organizations
  const fetchData = async () => {
    const isFirstLoad = !hasLoadedOnceRef.current;
    if (isFirstLoad) {
      setIsInitialLoading(true);
    } else {
      setIsContentLoading(true);
    }
    setError(null);
    
    try {
      // Load all products and organizations; filter by type in the UI only
      const [orgs, productsData] = await Promise.all([
        getOrganizationsForIntegration(),
        getProducts()
      ]);
      
      setOrganizations(orgs);
      setSelectedOrgs(orgs);
      
      // Sort products by name
      productsData.sort((a, b) => a.name.localeCompare(b.name));
      
      // Convert to enhanced products
      const enhancedProducts = productsData.map(p => ({
        ...p,
        integration_fields: p.integration_fields || []
      })) as EnhancedProduct[];
      
      setProducts(enhancedProducts);
      
      // If we have products and organizations, check their integration status
      if (enhancedProducts.length > 0 && orgs.length > 0) {
        const productIds = enhancedProducts.map(p => p.id || p._id as string);
        const orgIds = orgs.map(o => o.id);
        
        // Use optimized single batch request for all organizations and products
        const integrationBatchData = await getMultipleIntegrationsStatus(orgIds, productIds);
        
        // Process integration status
        const integrationStatusMap: Record<string, Record<string, boolean>> = {};
        const activationStatusMap: Record<string, Record<string, boolean>> = {};
        
        // Process batch data for all products and orgs
        for (const productId of productIds) {
          integrationStatusMap[productId] = {};
          activationStatusMap[productId] = {};
          for (const orgId of orgIds) {
            // Check if the integration exists
            const exists = integrationBatchData[productId]?.[orgId] || false;
            integrationStatusMap[productId][orgId] = exists;
            
            // Set activation status based on active_status field if available
            const isActive = integrationBatchData.active_status?.[productId]?.[orgId] || false;
            activationStatusMap[productId][orgId] = isActive;
          }
        }
        
        setIntegrationStatus(integrationStatusMap);
        setActivationStatus(activationStatusMap);
        
        // Get logger integrations
          try {
            const loggerCustomers = await getLoggerCustomers();
            const loggerIntegrationsMap: Record<string, string[]> = {};
            
            // Initialize map with empty arrays for all logger products
            enhancedProducts
              .filter(p => p.type === 'logger')
              .forEach(p => {
                const productId = p.id || p._id as string;
                loggerIntegrationsMap[productId] = [];
              });
            
            // Process logger customers to get integration status
            loggerCustomers.forEach(customer => {
              const orgId = customer.customer_id;
              
              // Add each logger product to the map
              customer.logger_products.forEach(productId => {
                if (!loggerIntegrationsMap[productId]) {
                  loggerIntegrationsMap[productId] = [];
                }
                
                if (!loggerIntegrationsMap[productId].includes(orgId)) {
                  loggerIntegrationsMap[productId].push(orgId);
                }
                
                // Also update the integration status map
                if (!integrationStatusMap[productId]) {
                  integrationStatusMap[productId] = {};
                }
                integrationStatusMap[productId][orgId] = true;
              });
            });
            
            setLoggerIntegrations(loggerIntegrationsMap);
            setIntegrationStatus(integrationStatusMap);
            
            // Get activation status for logger products using batch API
            if (loggerCustomers.length > 0) {
              const productIds = Object.keys(loggerIntegrationsMap);
              const orgIds = loggerCustomers.map(customer => customer.customer_id);
              
              if (productIds.length > 0 && orgIds.length > 0) {
                try {
                  const loggerActivationStatus = await getLoggerIntegrationsBatch(orgIds, productIds);
                  
                  // Merge logger activation status into the main activation status map
                  setActivationStatus(prev => {
                    const newStatus = { ...prev };
                    
                    for (const productId in loggerActivationStatus) {
                      if (!newStatus[productId]) {
                        newStatus[productId] = {};
                      }
                      
                      for (const orgId in loggerActivationStatus[productId]) {
                        newStatus[productId][orgId] = loggerActivationStatus[productId][orgId];
                      }
                    }
                    
                    return newStatus;
                  });
                } catch (err) {
                  console.error('Error fetching logger product activation status:', err);
                }
              }
            }
          } catch (err) {
            console.error('Error fetching logger customers:', err);
          }
        
        // Mark data as fully loaded since we fetched all in one request
        
        setDataFullyLoaded(true);
      }
    } catch (err) {
      console.error('Error loading integration data:', err);
      setError('Failed to load integration data. Please try again.');
    } finally {
      setIsInitialLoading(false);
      setIsContentLoading(false);
      hasLoadedOnceRef.current = true;
    }
  };
  
  // Fetch all data on page load only (tab/filter changes are client-side)
  useEffect(() => {
    fetchData();
  }, []);
  
  // Add a refresh handler to ensure complete data loading
  useEffect(() => {
    const handleRefresh = () => {
      console.log('Page refreshed, ensuring complete data load');
      setDataFullyLoaded(false);
      setLoadingAdditionalData(false);
    };
    
    // Listen for page visibility changes (refresh, tab switch, etc.)
    document.addEventListener('visibilitychange', handleRefresh);
    window.addEventListener('focus', handleRefresh);
    
    return () => {
      document.removeEventListener('visibilitychange', handleRefresh);
      window.removeEventListener('focus', handleRefresh);
    };
  }, []);
  
  // Function to load the remaining integration data after the page has loaded
  const loadRemainingIntegrationData = async (productIds: string[], orgIds: string[]) => {
    if (dataFullyLoaded || loadingAdditionalData) return;
    
    setLoadingAdditionalData(true);
    
    try {
      // Skip the first 20 orgs we've already loaded (matching the initial batch size)
      const remainingOrgIds = orgIds.slice(20);
      
      if (remainingOrgIds.length === 0 || productIds.length === 0) {
        setDataFullyLoaded(true);
        return;
      }
      
      // Load remaining data in smaller batches
      const batchSize = 5;
      for (let i = 0; i < remainingOrgIds.length; i += batchSize) {
        const batchOrgIds = remainingOrgIds.slice(i, i + batchSize);
        
        if (batchOrgIds.length === 0) break;
        
        // Fetch this batch of integration statuses
        const batchResults = await getMultipleIntegrationsStatus(batchOrgIds, productIds) as IntegrationBatchResponse;
        
        // Update the integration status with the new batch results
        setIntegrationStatus(prevStatus => {
          const newStatus = { ...prevStatus };
          
          // Merge the new batch results with the existing status
          for (const productId of productIds) {
            if (!newStatus[productId]) {
              newStatus[productId] = {};
            }
            
            if (batchResults[productId]) {
              for (const orgId of batchOrgIds) {
                newStatus[productId][orgId] = batchResults[productId][orgId] || false;
              }
            }
          }
          
          return newStatus;
        });
        
        // Update activation status based on the batch results
        setActivationStatus(prevStatus => {
          const newStatus = { ...prevStatus };
          
          // Check if active_status is included in the batch results
          if (batchResults.active_status) {
            // Merge the new active status results with the existing status
            for (const productId of productIds) {
              if (!newStatus[productId]) {
                newStatus[productId] = {};
              }
              
              if (batchResults.active_status[productId]) {
                for (const orgId of batchOrgIds) {
                  if (batchResults.active_status[productId][orgId] !== undefined) {
                    newStatus[productId][orgId] = batchResults.active_status[productId][orgId];
                  } else if (batchResults[productId] && batchResults[productId][orgId] !== undefined) {
                    // Fallback to integration status if active status not provided
                    newStatus[productId][orgId] = batchResults[productId][orgId];
                  }
                }
              }
            }
          } else {
            // If active_status not provided, update based on integration status
            for (const productId of productIds) {
              if (!newStatus[productId]) {
                newStatus[productId] = {};
              }
              
              if (batchResults[productId]) {
                for (const orgId of batchOrgIds) {
                  // Only set activation status for new integrations
                  if (batchResults[productId][orgId] && newStatus[productId][orgId] === undefined) {
                    newStatus[productId][orgId] = true; // Default to active for new integrations
                  }
                }
              }
            }
          }
          
          return newStatus;
        });
        
        // Add a small delay between batches to prevent UI from freezing
        await new Promise(resolve => setTimeout(resolve, 300));
      }
      
      setDataFullyLoaded(true);
      console.log('All remaining integration data loaded successfully');
    } catch (error) {
      console.error('Error loading additional integration data:', error);
      // If there's an error, try to load all remaining data at once as fallback
      try {
        console.log('Attempting fallback: loading all remaining data at once');
        const fallbackResults = await getMultipleIntegrationsStatus(orgIds, productIds);
        
        // Update integration status with fallback results
        setIntegrationStatus(prevStatus => {
          const newStatus = { ...prevStatus };
          for (const productId of productIds) {
            if (!newStatus[productId]) {
              newStatus[productId] = {};
            }
            if (fallbackResults[productId]) {
              for (const orgId of orgIds) {
                newStatus[productId][orgId] = fallbackResults[productId][orgId] || false;
              }
            }
          }
          return newStatus;
        });
        
        setDataFullyLoaded(true);
        console.log('Fallback loading completed successfully');
      } catch (fallbackError) {
        console.error('Fallback loading also failed:', fallbackError);
      }
    } finally {
      setLoadingAdditionalData(false);
    }
  };
  
  // Load additional data when expanding a product
  useEffect(() => {
    if (expandedProductId && !dataFullyLoaded && !loadingAdditionalData) {
      const productIds = products
        .map(product => (product.id || product._id) as string)
        .filter(Boolean);
      
      const orgIds = organizations.map(org => org.id);
      
      loadRemainingIntegrationData(productIds, orgIds);
    }
  }, [expandedProductId, dataFullyLoaded, loadingAdditionalData, products, organizations]);
  
  // Handle toggling a product expansion
  const toggleProductExpand = (productId: string) => {
    if (expandedProductId === productId) {
      setExpandedProductId(null);
    } else {
      setExpandedProductId(productId);
      // Clear search term when expanding a product to view customers
      setSearchTerm('');
    }
  };
  
  // Handle changing the selected organizations filter
  const handleOrgFilterChange = (orgs: Tenant[]) => {
    setSelectedOrgs(orgs);
  };
  
  // Toggle organization level filter
  const toggleLevelFilter = (level: string) => {
    setSelectedLevels(prevLevels => {
      if (prevLevels.includes(level)) {
        return prevLevels.filter(l => l !== level);
      } else {
        return [...prevLevels, level];
      }
    });
  };
  
  // Handle toggle client view mode
  const toggleClientViewMode = (mode: 'grid' | 'table') => {
    // Set the client view mode state
    setClientViewMode(mode);
    
    // Save the preference to localStorage
    try {
      localStorage.setItem('client-view-mode', mode);
      // Client view mode set
    } catch (error) {
      console.error('Error saving client view mode to localStorage:', error);
    }
  };
  
  // Handle integration configuration save
  const handleSaveConfig = (productId: string, orgId: string, values: Record<string, string | string[]>) => {
    // Saving integration configuration
    setIsLoadingDetails(true);
    
    // Remove the setTimeout and make the API call directly
    (async () => {
      try {
        // Check if this is an update or create
        let existingIntegration = null;
        
        try {
          existingIntegration = await getIntegrationValues(productId, orgId);
        } catch (fetchError) {
          // It's normal for this to 404 on a new integration
        }
        
        // Create or update the integration
        const response = await createProductIntegration({
          customer_id: orgId,
          product_id: productId,
          values
        });
        
        if (!response.success) {
          // Handle the error from the API
          console.error('Error response from API:', response);
          toast.error(response.message || 'Failed to save integration configuration');
          setIsLoadingDetails(false);
          return;
        }
        
        // Integration saved successfully
        
        // Show success message
        toast.success(existingIntegration ? 'Integration updated successfully' : 'Integration created successfully');
        
        // Update the integration status in the UI
        setIntegrationStatus(prev => {
          const newStatus = { ...prev };
          if (!newStatus[productId]) {
            newStatus[productId] = {};
          }
          newStatus[productId][orgId] = true;
          return newStatus;
        });
        
        // Set integration date to now
        setIntegrationDates(prev => {
          const newDates = { ...prev };
          if (!newDates[productId]) {
            newDates[productId] = {};
          }
          newDates[productId][orgId] = new Date().toISOString();
          return newDates;
        });
        
        // Ensure the activation status is set to true when a new integration is created
        // But don't change it when just updating an existing integration
        if (!existingIntegration) {
          setActivationStatus(prev => {
            const newStatus = { ...prev };
            if (!newStatus[productId]) {
              newStatus[productId] = {};
            }
            newStatus[productId][orgId] = true; // Activate by default when first integrated
            return newStatus;
          });
        }
        
        // Close the modal
        setConfigModalOpen(false);
      } catch (error: any) {
        console.error('Error saving integration configuration:', error);
        
        // Enhanced error message extraction
        let errorMessage = 'Failed to save integration configuration';
        
        if (error.response && error.response.data) {
          // Try to extract the error message from the response
          errorMessage = error.response.data.error || error.response.data.message || errorMessage;
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        // Show error toast with message from error if available
        toast.error(errorMessage);
      } finally {
        setIsLoadingDetails(false);
      }
    })();
  };
  
  // Open configuration modal
  const openConfigModal = (product: EnhancedProduct, org: Tenant) => {
    setSelectedProductForConfig(product);
    setSelectedOrgForConfig(org);
    setConfigModalOpen(true);
    // Clear search term when opening modal
    setSearchTerm('');
  };
  
  // Open logger configuration modal
  const openLoggerConfigModal = (product: Product, org: Tenant) => {
    setSelectedProductForLoggerConfig(product);
    setSelectedOrgForLoggerConfig(org);
    setLoggerConfigModalOpen(true);
    // Clear search term when opening modal
    setSearchTerm('');
  };
  
  // Toggle integration status
  const toggleIntegrationStatus = (productId: string, orgId: string, currentStatus: boolean) => {
    // Toggle integration status
    setIntegrationStatus(prev => {
      const newStatus = { ...prev };
      if (!newStatus[productId]) {
        newStatus[productId] = {};
      }
      newStatus[productId][orgId] = !currentStatus;
      
      // In a real app, you would call an API here
              // Toggle integration status
      
      return newStatus;
    });
    
    // If toggling to active, set integration date to now
    if (!currentStatus) {
      setIntegrationDates(prev => {
        const newDates = { ...prev };
        if (!newDates[productId]) {
          newDates[productId] = {};
        }
        newDates[productId][orgId] = new Date().toISOString();
        return newDates;
      });
    }
  };
  
  // Toggle activation status (separate from integration)
  const toggleActivationStatus = async (productId: string, orgId: string, currentlyActive: boolean) => {
    // Generate a unique key for this toggle
    const toggleKey = `${productId}_${orgId}`;
    
    // Set loading state for this specific toggle
    setLoadingToggles(prev => ({
      ...prev,
      [toggleKey]: true
    }));
    
    // Show loading state or disable the button temporarily if needed
            // Toggle activation status
    
    try {
      // Check if this is a logger product or a SaaS product
      const product = products.find(p => (p.id || p._id as string) === productId);
      
      if (!product) {
        throw new Error(`Product not found with ID: ${productId}`);
      }
      
      let response;
      
      // Use different API endpoints based on product type
      if (product.type === 'logger') {
        // For logger products, use the logger-specific API
        response = await updateLoggerCustomerStatus({
          customer_id: orgId,
          product_id: productId,
          is_active: !currentlyActive
        });
      } else {
        // For SaaS products, use the existing integration API
        response = await updateProductIntegrationStatus({
          customer_id: orgId,
          product_id: productId,
          is_active: !currentlyActive
        });
      }
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to update activation status');
      }
      
      // If API call was successful, update the UI state
      setActivationStatus(prev => {
        const newStatus = { ...prev };
        if (!newStatus[productId]) {
          newStatus[productId] = {};
        }
        newStatus[productId][orgId] = !currentlyActive;
        return newStatus;
      });
      
      // Show a success notification
      toast.success(`Successfully ${!currentlyActive ? 'activated' : 'deactivated'} integration`);
    } catch (error: any) {
      // If there was an error, don't update the UI state
      console.error('Failed to update integration activation status:', error);
      
      // Show an error notification
      toast.error(error.message || 'Failed to update activation status. Please try again.');
    } finally {
      // Clear loading state for this toggle
      setLoadingToggles(prev => ({
        ...prev,
        [toggleKey]: false
      }));
    }
  };
  
  // Filter displayed organizations based on selection and level
  const displayedOrganizations = organizations
    .filter(org => selectedLevels.includes(org.level)) // Filter by selected levels
    .filter(org => selectedOrgs.length === 0 || selectedOrgs.some(selected => selected.id === org.id)) // Filter by selected orgs
    .sort((a, b) => a.name.localeCompare(b.name)); // Ensure alphabetical sorting
  
  // Fix for list view - ensure we have properly sorted and filtered data
  const getFilteredOrganizationsForProduct = (productId: string) => {
    return displayedOrganizations
      .filter(org => searchTerm ? org.name.toLowerCase().includes(searchTerm.toLowerCase()) : true)
      .sort((a, b) => {
        // Sort by integration status first (integrated first)
        const aIntegrated = integrationStatus[productId]?.[a.id] || false;
        const bIntegrated = integrationStatus[productId]?.[b.id] || false;
        
        if (aIntegrated && !bIntegrated) return -1;
        if (!aIntegrated && bIntegrated) return 1;
        
        // Then sort alphabetically
        return a.name.localeCompare(b.name);
      });
  };
  
  // Filter products based on search term
  const filteredProducts = products
    .filter(product => 
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (product.tags?.productModule || '').toLowerCase().includes(searchTerm.toLowerCase())
    )
    .filter(product => {
      if (productTypeFilter === 'all') return true;
      return (product as any).type === productTypeFilter;
    });
  
  // Count how many organizations have integrated each product
  const getProductIntegrationCount = (productId: string) => {
    if (!integrationStatus[productId]) return 0;
    
    return Object.values(integrationStatus[productId]).filter(Boolean).length;
  };
  
  // Compute integration percentage for each product
  const getProductIntegrationPercentage = (productId: string) => {
    if (!integrationStatus[productId] || organizations.length === 0) return 0;
    
    const integratedCount = getProductIntegrationCount(productId);
    return Math.round((integratedCount / organizations.length) * 100);
  };
  
  // Add this effect when a product is expanded to initialize the selected organizations to show only integrated clients
  useEffect(() => {
    if (expandedProductId) {
      const productId = expandedProductId;
      
      // Show only integrated L3 clients by default when a product is expanded
      const integratedOrgs = organizations.filter(org => 
        org.level === 'L3' && integrationStatus[productId]?.[org.id] === true
      );
      
      if (integratedOrgs.length === 0) {
        // If no integrated L3 orgs found, just show all L3 orgs
        setSelectedOrgs(organizations.filter(org => org.level === 'L3'));
      } else {
        setSelectedOrgs(integratedOrgs);
      }
      
      // Default to L3 organizations
      setSelectedLevels(['L3']);
    }
  }, [expandedProductId, integrationStatus, organizations]);
  
  // Function to open the delete confirmation modal
  const openDeleteConfirmation = (productId: string, orgId: string, orgName: string) => {
    setDeleteInfo({ productId, orgId, orgName });
    setDeleteModalOpen(true);
  };

  // Function to actually perform the deletion
  const confirmDeleteIntegration = async () => {
    if (!deleteInfo) return;
    
    const { productId, orgId, orgName } = deleteInfo;
    
    // Create a unique key for this operation
    const toggleKey = `${productId}_${orgId}`;
    
    // Set loading state
    setLoadingToggles(prev => ({
      ...prev,
      [toggleKey]: true
    }));
    
    try {
      // Use the proper delete endpoint to permanently remove from database
      await deleteProductIntegration({
        customer_id: orgId,
        product_id: productId
      });
      
      // Update the integration status in the UI
      setIntegrationStatus(prev => {
        const newStatus = { ...prev };
        if (newStatus[productId]) {
          newStatus[productId][orgId] = false;
        }
        return newStatus;
      });
      
      // Remove integration date
      setIntegrationDates(prev => {
        const newDates = { ...prev };
        if (newDates[productId]) {
          delete newDates[productId][orgId];
        }
        return newDates;
      });
      
      // Set activation to false as well
      setActivationStatus(prev => {
        const newStatus = { ...prev };
        if (newStatus[productId]) {
          newStatus[productId][orgId] = false;
        }
        return newStatus;
      });
      
      // Show success message
      toast.success(`Integration permanently deleted for ${orgName}`, {
        icon: '🗑️',
        duration: 3000
      });
    } catch (error) {
      console.error('Failed to delete integration from database:', error);
      toast.error('Failed to delete integration from database. Please try again.', {
        duration: 4000
      });
    } finally {
      // Clear loading state
      setLoadingToggles(prev => ({
        ...prev,
        [toggleKey]: false
      }));
      
      // Close the modal
      setDeleteModalOpen(false);
      setDeleteInfo(null);
    }
  };
  
  // Component for the table view delete button (icon only)
  const TableDeleteButton: React.FC<{
    productId: string;
    orgId: string;
    orgName: string;
    isLoading: boolean;
  }> = ({ productId, orgId, orgName, isLoading }) => {
    // Only show delete button if user has permission to manage integrations
    if (!canManageIntegrations()) {
      return null;
    }
    
    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          openDeleteConfirmation(productId, orgId, orgName);
        }}
        className="premium-button-outline text-status-danger hover:text-red-400 hover:border-red-500/50 py-1 px-2 text-xs border border-red-500/30"
        disabled={isLoading}
        title="Delete Integration from Database"
      >
        {isLoading ? (
          <Loader2 size={12} className="animate-spin" />
        ) : (
          <Trash2 size={12} />
        )}
      </button>
    );
  };

  // Component for the grid view delete button (with text)
  const GridDeleteButton: React.FC<{
    productId: string;
    orgId: string;
    orgName: string;
    isLoading: boolean;
  }> = ({ productId, orgId, orgName, isLoading }) => {
    // Only show delete button if user has permission to manage integrations
    if (!canManageIntegrations()) {
      return null;
    }
    
    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          openDeleteConfirmation(productId, orgId, orgName);
        }}
        className="premium-button-outline text-status-danger hover:text-red-400 hover:border-red-500/50 py-1 px-3 text-xs border border-red-500/30"
        disabled={isLoading}
      >
        {isLoading ? (
          <span className="flex items-center">
            <Loader2 size={14} className="animate-spin mr-1" />
            Deleting...
          </span>
        ) : (
          <span className="flex items-center">
            <Trash2 size={14} className="mr-1" />
            Delete
          </span>
        )}
      </button>
    );
  };
  
  const showContentLoading = isInitialLoading || isContentLoading;

  // Check if an organization has a logger integration for a specific product
  const hasLoggerIntegration = (productId: string, orgId: string): boolean => {
    return loggerIntegrations[productId]?.includes(orgId) || false;
  };

  // Handle config file download for logger products
  const handleDownloadLoggerConfig = async (product: Product, org: Tenant) => {
    if (!org.id) {
      toast.error('Customer ID is required');
      return;
    }
    
    // Find all logger products that are integrated with this organization
    const integratedLoggerProducts = products.filter(p => {
      const productId = p.id || p._id as string;
      return p.type === 'logger' && integrationStatus[productId]?.[org.id];
    });
    
    if (integratedLoggerProducts.length === 0) {
      toast.error('No logger products integrated with this organization');
      return;
    }
    
    // Set the selected organization for download
    setSelectedOrgForDownload(org);
    
    // Set all integrated products as selected by default
    const integratedProductIds = integratedLoggerProducts.map(p => p.id || p._id as string);
    setProductsForDownload(integratedProductIds);
    
    // Open the download modal
    setDownloadModalOpen(true);
  };
  
  // Handle product selection/deselection for download
  const handleProductToggleForDownload = (productId: string) => {
    setProductsForDownload(prev => {
      if (prev.includes(productId)) {
        return prev.filter(id => id !== productId);
      } else {
        return [...prev, productId];
      }
    });
  };
  
  // Handle download with selected products
  const handleDownloadWithSelectedProducts = async () => {
    if (!selectedOrgForDownload || productsForDownload.length === 0) {
      toast.error('Please select at least one product for download');
      return;
    }
    
    setIsDownloading(true);
    
    try {
      // Get the logger type - default to 'logger01'
      const loggerType = 'logger01'; // You might want to fetch this from the backend
      
      // Generate and download the config file with specific products
      const config = await generateConfigFile(
        selectedOrgForDownload.id, 
        loggerType,
        productsForDownload.join(',')
      );
      
      // Download the file
      const productSuffix = productsForDownload.length > 1 ? `_${productsForDownload.length}_products` : '';
      
      downloadConfigFile(
        config.template_content,
        `${selectedOrgForDownload.name.replace(/\s+/g, '_').toLowerCase()}${productSuffix}_rsyslog.conf`
      );
      
      toast.success('Configuration file downloaded');
      setDownloadModalOpen(false);
    } catch (error) {
      console.error('Error generating config file:', error);
      toast.error('Failed to generate configuration file');
    } finally {
      setIsDownloading(false);
    }
  };
  
  // Clear all filters
  const clearFilters = () => {
    setProductTypeFilter('all');
    setSearchTerm('');
  };

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Count products by type
  const productCounts = {
    all: products.length,
    saas: products.filter(p => (p as any).type === 'saas').length,
    logger: products.filter(p => (p as any).type === 'logger').length
  };
  
  // Handle viewing logger config
  const handleViewLoggerConfig = async (product: Product, org: Tenant) => {
    if (!org.id) {
      toast.error('Customer ID is required');
      return;
    }
    
    // Set loading state for this action
    const toggleKey = `view_${product.id}_${org.id}`;
    setIsLoadingConfig(prev => ({
      ...prev,
      [toggleKey]: true
    }));
    
    try {
      // Get the logger type - default to 'logger01'
      const loggerType = 'logger01'; // You might want to fetch this from the backend
      
      // Generate the config file
      const config = await generateConfigFile(org.id, loggerType);
      
      // Set the config content and organization name for the modal
      setConfigContent(config.template_content);
      setConfigOrgName(org.name);
      
      // Open the view config modal
      setViewConfigModalOpen(true);
    } catch (error) {
      console.error('Error generating config file:', error);
      toast.error('Failed to generate configuration file');
    } finally {
      // Clear loading state
      setIsLoadingConfig(prev => ({
        ...prev,
        [toggleKey]: false
      }));
    }
  };

  // Update the logger grid view to include the view button
  const renderLoggerGridView = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 relative z-0">
      {organizations
        .filter(org => 
          selectedLevels.includes(org.level) && 
          org.name.toLowerCase().includes(searchTerm.toLowerCase())
        )
        .map(org => {
          // Count how many logger products this org has integrated
          const loggerProducts = products.filter(p => p.type === 'logger');
          const integratedCount = loggerProducts.reduce((count, product) => {
            const productId = product.id || product._id as string;
            return count + (integrationStatus[productId]?.[org.id] ? 1 : 0);
          }, 0);

          return (
            <div 
              key={org.id} 
              className="card relative p-4 hover:border-primary/50 transition-colors flex flex-col min-h-[200px]"
            >
              {/* Organization card main content */}
              <div className="flex justify-between items-start mb-2 relative z-10">
                {/* Organization info */}
                <div className="flex items-center">
                  <div className="h-12 w-12 mr-3 flex items-center justify-center bg-background-hover rounded flex-shrink-0">
                    <Building2 size={24} className="text-text-muted-dark" />
                  </div>
                  
                  <div>
                    <h3 className="font-medium text-sm">{org.name}</h3>
                    <div className="flex flex-wrap items-center mt-1 gap-2">
                      <span className={`text-xs px-2 py-0.5 rounded ${
                          org.level === 'L3' ? 'bg-green-700/20 text-green-300' :
                          org.level === 'L2' ? 'bg-purple-700/20 text-purple-300' :
                          'bg-blue-700/20 text-blue-300'
                        }`}>
                          {org.level}
                        </span>
                      <code className="text-xs bg-background-darker px-1.5 py-0.5 rounded">{org.tenant_code}</code>
                    </div>
                  </div>
                </div>
                
                {/* Integration count indicator */}
                <div className="text-right">
                  <div className="text-xs text-text-muted-dark mb-1">Logger Products</div>
                  <div className="text-sm font-semibold rounded-full bg-green-600/20 px-2.5 py-0.5 text-green-400 border border-green-600/30 inline-block">
                    {integratedCount}
                  </div>
                </div>
              </div>

              <div className="border-t border-border-dark pt-2 mb-2 flex-grow">
                <div className="text-xs text-text-muted-dark mb-2">
                  Integrated Logger Products
                </div>
                <div className="space-y-1">
                  {loggerProducts.filter(product => {
                    const productId = product.id || product._id as string;
                    return integrationStatus[productId]?.[org.id];
                  }).slice(0, 3).map((product) => (
                    <div key={product.id || product._id as string} className="flex items-center">
                      <div className="w-2 h-2 rounded-full bg-green-500 mr-2"></div>
                      <span className="text-xs">{product.name}</span>
                    </div>
                  ))}
                  {loggerProducts.filter(product => {
                    const productId = product.id || product._id as string;
                    return integrationStatus[productId]?.[org.id];
                  }).length === 0 && (
                    <div className="text-xs text-text-muted-dark">
                      No logger products integrated
                    </div>
                  )}
                  {loggerProducts.filter(product => {
                    const productId = product.id || product._id as string;
                    return integrationStatus[productId]?.[org.id];
                  }).length > 3 && (
                    <div className="text-xs text-primary">
                      +{loggerProducts.filter(product => {
                        const productId = product.id || product._id as string;
                        return integrationStatus[productId]?.[org.id];
                      }).length - 3} more products
                    </div>
                  )}
                </div>
              </div>
              
              {/* Action buttons */}
              <div className="border-t border-border-dark pt-3 mt-auto flex justify-end space-x-2">
                {integratedCount > 0 && (
                  <button
                    onClick={() => {
                      // Find a logger product to use for viewing config
                      const loggerProduct = products.find(p => p.type === 'logger');
                      if (loggerProduct) {
                        handleViewLoggerConfig(loggerProduct, org);
                      } else {
                        toast.error('No configured logger products available');
                      }
                    }}
                    disabled={integratedCount === 0}
                    className={`premium-button-secondary text-sm py-1.5 ${
                      integratedCount === 0 ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                    title={integratedCount === 0 ? 'Configure logger products first' : 'View configuration'}
                  >
                    {isLoadingConfig[`view_${products.find(p => p.type === 'logger')?.id}_${org.id}`] ? (
                      <Loader2 size={14} className="mr-1 animate-spin" />
                    ) : (
                      <Eye size={14} className="mr-1" />
                    )}
                    View
                  </button>
                )}
                <button
                  onClick={() => {
                    // Find a logger product to use for downloading config
                    const loggerProduct = products.find(p => p.type === 'logger');
                    if (loggerProduct && integratedCount > 0) {
                      handleDownloadLoggerConfig(loggerProduct, org);
                    } else {
                      toast.error('No configured logger products available');
                    }
                  }}
                  disabled={integratedCount === 0}
                  className={`premium-button-secondary text-sm py-1.5 ${
                    integratedCount === 0 ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  title={integratedCount === 0 ? 'Configure logger products first' : 'Download configuration'}
                >
                  <Download size={14} className="mr-1" />
                  Download
                </button>
                {canManageIntegrations() && (
                  <button
                    onClick={() => {
                      // Find a logger product to configure
                      const loggerProduct = products.find(p => p.type === 'logger');
                      if (loggerProduct) {
                        openLoggerConfigModal(loggerProduct, org);
                      } else {
                        toast.error('No logger products available for configuration');
                      }
                    }}
                    className="premium-button-primary text-sm py-1.5"
                  >
                    Configure
                  </button>
                )}
              </div>
            </div>
          );
        })}
      
      {organizations.filter(org => 
        selectedLevels.includes(org.level) && 
        org.name.toLowerCase().includes(searchTerm.toLowerCase())
      ).length === 0 && (
        <div className="col-span-3 text-center py-8">
          <Building2 size={32} className="text-gray-500 mb-2 mx-auto" />
          <p className="text-text-muted-dark">No customers match your search</p>
        </div>
      )}
    </div>
  );

  // Update the logger table view to include the view button
  const renderLoggerTableView = () => (
    <div className="rounded-lg border border-border-dark overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-background-hover border-b border-border-dark">
              <th className="px-4 py-3 text-left font-medium text-sm">Organization</th>
              <th className="px-4 py-3 text-left font-medium text-sm">Level</th>
              <th className="px-4 py-3 text-left font-medium text-sm">Tenant Code</th>
              <th className="px-4 py-3 text-center font-medium text-sm">Logger Products</th>
              <th className="px-4 py-3 text-right font-medium text-sm">Actions</th>
            </tr>
          </thead>
          <tbody>
            {organizations.filter(org => 
              selectedLevels.includes(org.level) && 
              org.name.toLowerCase().includes(searchTerm.toLowerCase())
            ).length > 0 ? (
              organizations
                .filter(org => 
                  selectedLevels.includes(org.level) && 
                  org.name.toLowerCase().includes(searchTerm.toLowerCase())
                )
                .map(org => {
                  // Count how many logger products this org has integrated
                  const loggerProducts = products.filter(p => p.type === 'logger');
                  const integratedCount = loggerProducts.reduce((count, product) => {
                    const productId = product.id || product._id as string;
                    return count + (integrationStatus[productId]?.[org.id] ? 1 : 0);
                  }, 0);
                  
                  // Define colors and badges for different levels
                  const levelBadges = {
                    'L0': { bg: 'bg-blue-600', text: 'L0' },
                    'L2': { bg: 'bg-purple-600', text: 'L2' },
                    'L3': { bg: 'bg-green-600', text: 'L3' }
                  };
                  
                  const levelBadge = levelBadges[org.level as keyof typeof levelBadges] || { bg: 'bg-gray-600', text: org.level };
                  
                  return (
                    <tr key={org.id} className="border-t border-border-dark hover:bg-background-hover/40 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center">
                          <Building2 size={16} className="mr-2 text-text-muted-dark flex-shrink-0" />
                          <div className="font-medium">{org.name}</div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 text-xs text-white rounded-md ${levelBadge.bg}`}>
                          {levelBadge.text}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <code className="text-xs bg-background-darker px-1.5 py-0.5 rounded">{org.tenant_code}</code>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="text-sm font-semibold rounded-full bg-green-600/20 px-2.5 py-0.5 text-green-400 border border-green-600/30 inline-block">
                          {integratedCount}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end space-x-2">
                          {integratedCount > 0 && (
                            <button
                              onClick={() => {
                                // Find a logger product to use for viewing config
                                const loggerProduct = products.find(p => p.type === 'logger');
                                if (loggerProduct) {
                                  handleViewLoggerConfig(loggerProduct, org);
                                } else {
                                  toast.error('No configured logger products available');
                                }
                              }}
                              disabled={integratedCount === 0}
                              className={`premium-button-secondary text-xs py-1 px-2 ${
                                integratedCount === 0 ? 'opacity-50 cursor-not-allowed' : ''
                              }`}
                              title={integratedCount === 0 ? 'Configure logger products first' : 'View configuration'}
                            >
                              {isLoadingConfig[`view_${products.find(p => p.type === 'logger')?.id}_${org.id}`] ? (
                                <Loader2 size={12} className="mr-1 animate-spin" />
                              ) : (
                                <Eye size={12} className="mr-1" />
                              )}
                              View
                            </button>
                          )}
                          <button
                            onClick={() => {
                              // Find a logger product to use for downloading config
                              const loggerProduct = products.find(p => p.type === 'logger');
                              if (loggerProduct && integratedCount > 0) {
                                handleDownloadLoggerConfig(loggerProduct, org);
                              } else {
                                toast.error('No configured logger products available');
                              }
                            }}
                            disabled={integratedCount === 0}
                            className={`premium-button-secondary text-xs py-1 px-2 ${
                              integratedCount === 0 ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                            title={integratedCount === 0 ? 'Configure logger products first' : 'Download configuration'}
                          >
                            <Download size={12} className="mr-1" />
                            Download
                          </button>
                          {canManageIntegrations() && (
                            <button
                              onClick={() => {
                                // Find a logger product to configure
                                const loggerProduct = products.find(p => p.type === 'logger');
                                if (loggerProduct) {
                                  openLoggerConfigModal(loggerProduct, org);
                                } else {
                                  toast.error('No logger products available for configuration');
                                }
                              }}
                              className="premium-button-primary text-xs py-1 px-3"
                            >
                              Configure
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
            ) : (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center">
                  <div className="flex flex-col items-center">
                    <Building2 size={24} className="mb-2 text-gray-500" />
                    <span className="text-text-muted-dark">No customers match your search</span>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  // Also update the expanded product view to include the view button for logger products
  const updateProductDetailView = () => {
    // Add the view button in the product detail view for logger products
    if (expandedProductId) {
      const product = products.find(p => (p.id || p._id as string) === expandedProductId);
      if (product && product.type === 'logger') {
        // Add view button in both grid and table views
        // This is handled in the existing code by updating the rendering logic
      }
    }
  };

  // Add a helper function to map EnhancedProduct to LoggerProduct
  const mapToLoggerProduct = (product: EnhancedProduct): LoggerProduct => {
    return {
      id: product.id || product._id as string || '',
      product_name: product.name || '',
      product_type: product.type || 'logger',
      log_type: product.tags?.logType || 'default',
      template_name: product.logger_config?.template_name || 'default',
      queue_size: product.logger_config?.queue_size || '1000',
      max_disk_space: product.logger_config?.max_disk_space || '1G',
      worker_threads: product.logger_config?.worker_threads || '1',
      filename: product.logger_config?.filename || 'rsyslog.conf',
      status: (product.logger_config?.status || 'active') as 'active' | 'inactive',
      keywords: product.logger_config?.keywords || []
    };
  };

  return (
    <div className="container mx-auto px-4 py-6 max-w-7xl">
      {/* Header section with gradient background */}
      <div className="mb-6 rounded-lg overflow-hidden relative z-10">
        <div className="bg-gradient-to-b from-gray-900/90 via-indigo-900/20 to-transparent p-6 rounded-xl backdrop-blur-sm border border-indigo-800/20 shadow-lg">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-300 to-indigo-300">Product Integrations</h1>
              <p className="text-gray-400 mt-1">
                Manage integrations between products and organizations
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                className={`relative z-10 py-2 px-4 transition-all duration-200 rounded-md flex items-center ${
                  activeView === 'products'
                    ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg'
                    : 'bg-background-card border border-border-dark text-gray-300 hover:bg-background-hover'
                }`}
                onClick={() => {
                  setActiveView('products');
                  localStorage.setItem('integration-view', 'products');
                }}
              >
                <Boxes size={16} className="mr-2" />
                Products
              </button>
              <button
                className={`relative z-10 py-2 px-4 transition-all duration-200 rounded-md flex items-center ${
                  activeView === 'logger'
                    ? 'bg-gradient-to-r from-green-600 to-teal-600 text-white shadow-lg'
                    : 'bg-background-card border border-border-dark text-gray-300 hover:bg-background-hover'
                }`}
                onClick={() => {
                  setActiveView('logger');
                  localStorage.setItem('integration-view', 'logger');
                }}
              >
                <Layers size={16} className="mr-2" />
                Logger
              </button>
              <button
                className={`relative z-10 py-2 px-4 transition-all duration-200 rounded-md flex items-center ${
                  activeView === 'stats'
                    ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg'
                    : 'bg-background-card border border-border-dark text-gray-300 hover:bg-background-hover'
                }`}
                onClick={() => {
                  setActiveView('stats');
                  localStorage.setItem('integration-view', 'stats');
                }}
              >
                <BarChart3 size={16} className="mr-2" />
                Statistics
              </button>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-status-danger/10 border border-status-danger/20 text-status-danger rounded-md p-4 mb-6">
          <p>{error}</p>
        </div>
      )}

      {/* Enhanced Filter and Search Bar */}
      <div className="mb-6 bg-background-card border border-card-border rounded-lg p-4">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search Bar */}
          <div className="relative flex-grow">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search size={18} className="text-gray-400" />
            </div>
            <input
              type="text"
              className="w-full pl-10 pr-4 py-2 bg-background-hover border border-border-dark rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
              placeholder={activeView === 'logger' ? "Search customers by name..." : "Search products by name or module..."}
              value={searchTerm}
              onChange={handleSearchChange}
            />
            {searchTerm && (
              <button
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setSearchTerm('')}
              >
                <X size={16} className="text-gray-400 hover:text-white transition-colors" />
              </button>
            )}
          </div>

          {/* Filter Toggle Button */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center px-4 py-2 rounded-lg border transition-all ${
              showFilters 
                ? 'bg-primary/20 border-primary/40 text-primary-300' 
                : 'border-border-dark bg-background-hover text-gray-300 hover:bg-background-darker'
            }`}
          >
            <SlidersHorizontal size={16} className="mr-2" />
            Filters
            {((activeView === 'products' && productTypeFilter !== 'all') || 
              (activeView === 'logger' && selectedLevels.length < availableLevels.length)) && (
              <span className="ml-2 bg-primary/30 text-primary-300 text-xs py-0.5 px-2 rounded-full">
                1
              </span>
            )}
          </button>
        </div>

        {/* Expanded Filter Options */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-border-dark animate-fadeIn">
            {activeView === 'products' ? (
              /* Product Type Filters for Products view */
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <div className="flex items-center text-gray-400 mr-2">
                <Filter size={16} className="mr-1" />
                <span className="text-sm">Product Type:</span>
              </div>
              
              <div className="flex flex-wrap gap-2">
                <button
                  className={`px-4 py-1.5 text-sm rounded-full transition-all flex items-center ${
                    productTypeFilter === 'all' 
                      ? 'bg-primary text-white shadow-md shadow-primary/20' 
                      : 'bg-background-hover text-text-muted hover:bg-background-darker border border-border-dark'
                  }`}
                  onClick={() => setProductTypeFilter('all')}
                >
                  All
                  <span className="ml-2 bg-white/20 text-white text-xs py-0.5 px-2 rounded-full">
                    {productCounts.all}
                  </span>
                </button>
                <button
                  className={`px-4 py-1.5 text-sm rounded-full transition-all flex items-center ${
                    productTypeFilter === 'saas' 
                      ? 'bg-blue-600 text-white shadow-md shadow-blue-500/20' 
                      : 'bg-background-hover text-text-muted hover:bg-background-darker border border-border-dark'
                  }`}
                  onClick={() => setProductTypeFilter('saas')}
                >
                  SaaS
                  <span className={`ml-2 text-xs py-0.5 px-2 rounded-full ${
                    productTypeFilter === 'saas'
                      ? 'bg-white/20 text-white'
                      : 'bg-blue-700/20 text-blue-300'
                  }`}>
                    {productCounts.saas}
                  </span>
                </button>
                <button
                  className={`px-4 py-1.5 text-sm rounded-full transition-all flex items-center ${
                    productTypeFilter === 'logger' 
                      ? 'bg-green-600 text-white shadow-md shadow-green-500/20' 
                      : 'bg-background-hover text-text-muted hover:bg-background-darker border border-border-dark'
                  }`}
                  onClick={() => setProductTypeFilter('logger')}
                >
                  Logger
                  <span className={`ml-2 text-xs py-0.5 px-2 rounded-full ${
                    productTypeFilter === 'logger'
                      ? 'bg-white/20 text-white'
                      : 'bg-green-700/20 text-green-300'
                  }`}>
                    {productCounts.logger}
                  </span>
                </button>
              </div>
              </div>
            ) : activeView === 'logger' ? (
              /* Organization Level Filters for Logger view */
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <div className="flex items-center text-gray-400 mr-2">
                  <Building2 size={16} className="mr-1" />
                  <span className="text-sm">Organization Level:</span>
            </div>
                
                <div className="flex flex-wrap gap-2">
                  {availableLevels.map(level => (
                    <button
                      key={level}
                      onClick={() => toggleLevelFilter(level)}
                      className={`px-4 py-1.5 text-sm rounded-full transition-all flex items-center ${
                        selectedLevels.includes(level)
                          ? level === 'L0' ? 'bg-blue-600 text-white shadow-md shadow-blue-500/20' 
                          : level === 'L2' ? 'bg-purple-600 text-white shadow-md shadow-purple-500/20' 
                          : 'bg-green-600 text-white shadow-md shadow-green-500/20'
                          : 'bg-background-hover text-text-muted hover:bg-background-darker border border-border-dark'
                      }`}
                    >
                      {level}
                      <span className={`ml-2 text-xs py-0.5 px-2 rounded-full ${
                        selectedLevels.includes(level)
                          ? 'bg-white/20 text-white'
                          : level === 'L0' ? 'bg-blue-700/20 text-blue-300'
                          : level === 'L2' ? 'bg-purple-700/20 text-purple-300'
                          : 'bg-green-700/20 text-green-300'
                      }`}>
                        {organizations.filter(org => org.level === level).length}
                      </span>
                    </button>
                  ))}
                  <button
                    onClick={() => setSelectedLevels([...availableLevels])}
                    className={`px-4 py-1.5 text-sm rounded-full transition-all flex items-center ${
                      selectedLevels.length === availableLevels.length
                        ? 'bg-primary text-white shadow-md shadow-primary/20'
                        : 'bg-background-hover text-text-muted hover:bg-background-darker border border-border-dark'
                    }`}
                  >
                    All Levels
                    <span className={`ml-2 text-xs py-0.5 px-2 rounded-full ${
                      selectedLevels.length === availableLevels.length
                        ? 'bg-white/20 text-white'
                        : 'bg-gray-700/20 text-gray-300'
                    }`}>
                      {organizations.length}
                    </span>
                  </button>
                </div>
              </div>
            ) : null}

            {/* Clear Filters Button */}
            {((activeView === 'products' && productTypeFilter !== 'all') || 
               (activeView === 'logger' && selectedLevels.length < availableLevels.length) || 
               searchTerm) && (
              <button
                onClick={() => {
                  if (activeView === 'products') {
                    setProductTypeFilter('all');
                  } else if (activeView === 'logger') {
                    setSelectedLevels([...availableLevels]);
                  }
                  setSearchTerm('');
                }}
                className="ml-auto text-xs text-gray-400 hover:text-white flex items-center border border-border-dark bg-background-hover hover:bg-background-darker px-3 py-1.5 rounded-md transition-colors"
              >
                <X size={14} className="mr-1" />
                Clear Filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Results Summary */}
      <div className="mb-4 flex justify-between items-center">
        <div className="text-sm text-gray-400">
          {activeView === 'products' ? (
            <>
          Showing <span className="text-white font-medium">{filteredProducts.length}</span> of <span className="text-white font-medium">{products.length}</span> products
          {(productTypeFilter !== 'all' || searchTerm) && (
            <span className="ml-1">
              with filters
              {productTypeFilter !== 'all' && (
                <span className="ml-1 bg-primary/20 text-primary-300 text-xs py-0.5 px-2 rounded-full">
                  {productTypeFilter}
                </span>
              )}
              {searchTerm && (
                <span className="ml-1 bg-blue-700/20 text-blue-300 text-xs py-0.5 px-2 rounded-full">
                  "{searchTerm}"
                </span>
              )}
            </span>
              )}
            </>
          ) : activeView === 'logger' ? (
            <>
              Showing <span className="text-white font-medium">
                {organizations.filter(org => 
                  selectedLevels.includes(org.level) && 
                  org.name.toLowerCase().includes(searchTerm.toLowerCase())
                ).length}
              </span> customers
              {(selectedLevels.length < availableLevels.length || searchTerm) && (
                <span className="ml-1">
                  with filters
                  {selectedLevels.length < availableLevels.length && (
                    <span className="ml-1 bg-primary/20 text-primary-300 text-xs py-0.5 px-2 rounded-full">
                      {selectedLevels.join(', ')}
                    </span>
                  )}
                  {searchTerm && (
                    <span className="ml-1 bg-blue-700/20 text-blue-300 text-xs py-0.5 px-2 rounded-full">
                      "{searchTerm}"
                    </span>
                  )}
                </span>
              )}
            </>
          ) : (
            <>Statistics View</>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          <button
            className={`p-2 rounded-md border ${
              clientViewMode === 'grid'
                ? 'bg-primary/20 border-primary/40 text-primary-300'
                : 'border-border-dark bg-background-hover text-gray-400 hover:text-gray-200'
            }`}
            onClick={() => toggleClientViewMode('grid')}
            title="Grid View"
            aria-label="Switch to Grid View"
          >
            <LayoutGrid size={18} />
          </button>
          <button
            className={`p-2 rounded-md border ${
              clientViewMode === 'table'
                ? 'bg-primary/20 border-primary/40 text-primary-300'
                : 'border-border-dark bg-background-hover text-gray-400 hover:text-gray-200'
            }`}
            onClick={() => toggleClientViewMode('table')}
            title="Table View"
            aria-label="Switch to Table View"
          >
            <List size={18} />
          </button>
        </div>
      </div>

      {showContentLoading ? (
        <ContentLoader
          message={isInitialLoading ? 'Loading integrations...' : 'Updating...'}
          size="lg"
        />
      ) : error ? (
        <div className="card">
          <div className="flex flex-col items-center justify-center py-8">
            <h2 className="text-xl font-semibold mb-2">{error}</h2>
            <button
              onClick={() => fetchData()}
              className="premium-button-primary mt-4"
            >
              Try Again
            </button>
          </div>
        </div>
      ) : products.length === 0 ? (
        <div className="card">
          <div className="flex flex-col items-center justify-center py-12">
            <Boxes size={48} className="text-gray-500 mb-4" />
            <h2 className="text-xl font-semibold mb-2">No Products Available</h2>
            <p className="text-text-muted-dark text-center max-w-md mb-6">
              There are no products available for integration. Please add products with integration fields first.
            </p>
            <Link to="/products/onboarding" className="premium-button-primary">
              Add New Product
            </Link>
          </div>
        </div>
      ) : organizations.length === 0 ? (
        <div className="card">
          <div className="flex flex-col items-center justify-center py-12">
            <Building2 size={48} className="text-gray-500 mb-4" />
            <h2 className="text-xl font-semibold mb-2">No Organizations Available</h2>
            <p className="text-text-muted-dark text-center max-w-md mb-6">
              There are no L3 organizations available for integration. Please add organizations first.
            </p>
            <Link to="/tenants/new" className="premium-button-primary">
              Add New Organization
            </Link>
          </div>
        </div>
      ) : activeView === 'products' ? (
        clientViewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 relative z-0">
          {filteredProducts.map(product => {
            const productId = product.id || product._id as string;
            const integrationCount = getProductIntegrationCount(productId);
            
            return (
              <div 
                key={productId} 
                className="card relative p-4 hover:border-primary/50 transition-colors cursor-pointer flex flex-col min-h-[200px]"
                onClick={() => toggleProductExpand(productId)}
              >
                {/* Product card main content */}
                <div className="flex justify-between items-start mb-2 relative z-10">
                  {/* Product info */}
                  <div className="flex items-center">
                    {product.logo_url ? (
                      <img 
                        src={product.logo_url}
                        alt={product.name}
                        className="h-12 w-12 mr-3 object-contain bg-white rounded p-1 flex-shrink-0"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = '';
                          (e.target as HTMLImageElement).classList.add('hidden');
                        }}
                      />
                    ) : (
                      <div className="h-12 w-12 mr-3 flex items-center justify-center bg-background-hover rounded flex-shrink-0">
                        <span className="text-lg font-bold text-text-muted-dark">
                          {product.name.substring(0, 2).toUpperCase()}
                        </span>
                      </div>
                    )}
                    
                    <div>
                      <h3 className="font-medium text-sm">{product.name}</h3>
                      <div className="flex flex-wrap items-center mt-1 gap-2">
                        {product.tags?.productModule && (
                          <span className="text-xs px-2 py-0.5 bg-primary-700/20 text-primary-300 rounded">
                            {product.tags.productModule}
                          </span>
                        )}
                        {/* Product type badge */}
                        {product.type && (
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            product.type === 'logger' 
                              ? 'bg-green-700/20 text-green-300' 
                              : 'bg-blue-700/20 text-blue-300'
                          }`}>
                            {product.type === 'logger' ? 'Logger' : 'SaaS'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Replace Integration rate indicator with integrated count */}
                  <div className="text-right">
                    <div className="text-xs text-text-muted-dark mb-1">Integrated</div>
                    <div className="text-sm font-semibold rounded-full bg-green-600/20 px-2.5 py-0.5 text-green-400 border border-green-600/30 inline-block">
                      {integrationCount}
                    </div>
                  </div>
                </div>
                
                <div className="border-t border-border-dark pt-2 mb-2 flex-grow">
                  <div className="text-xs text-text-muted-dark mb-2">
                    {product.integration_fields.length} integration field{product.integration_fields.length !== 1 ? 's' : ''}
                  </div>
                  <div className="space-y-1">
                    {product.integration_fields.slice(0, 3).map((field) => (
                      <div key={field.field_key} className="flex items-center">
                        <div className="w-2 h-2 rounded-full bg-gray-500 mr-2"></div>
                        <span className="text-xs">
                          {field.label}
                          {field.is_required && (
                            <span className="text-status-danger ml-1">*</span>
                          )}
                        </span>
                      </div>
                    ))}
                    {product.integration_fields.length > 3 && (
                      <div className="text-xs text-primary">
                        +{product.integration_fields.length - 3} more fields
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Click indicator instead of button */}
                <div className="border-t border-border-dark pt-3 mt-auto flex justify-center items-center">
                  <span className="text-xs flex items-center bg-gray-800/50 hover:bg-gray-700/70 px-3 py-1.5 rounded-md text-blue-400 transition-colors">
                    Click to view client customers
                    <ChevronRight size={14} className="ml-1" />
                  </span>
                </div>
              </div>
            );
          })}
          </div>
        ) : (
          // List View for Products
          <div className="rounded-lg border border-border-dark overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-background-hover border-b border-border-dark">
                    <th className="px-4 py-3 text-left font-medium text-sm">Product</th>
                    <th className="px-4 py-3 text-left font-medium text-sm">Type</th>
                    <th className="px-4 py-3 text-left font-medium text-sm">Module</th>
                    <th className="px-4 py-3 text-left font-medium text-sm">Integration Fields</th>
                    <th className="px-4 py-3 text-center font-medium text-sm">Integrations</th>
                    <th className="px-4 py-3 text-right font-medium text-sm">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.length > 0 ? (
                    filteredProducts.map(product => {
                      const productId = product.id || product._id as string;
                      const integrationCount = getProductIntegrationCount(productId);
                      
                      return (
                        <tr key={productId} className="border-t border-border-dark hover:bg-background-hover/40 cursor-pointer transition-colors" onClick={() => toggleProductExpand(productId)}>
                          <td className="px-4 py-3">
                            <div className="flex items-center">
                              {product.logo_url ? (
                                <img 
                                  src={product.logo_url}
                                  alt={product.name}
                                  className="h-10 w-10 mr-3 object-contain bg-white rounded p-1 flex-shrink-0"
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).src = '';
                                    (e.target as HTMLImageElement).classList.add('hidden');
                                  }}
                                />
                              ) : (
                                <div className="h-10 w-10 mr-3 flex items-center justify-center bg-background-hover rounded flex-shrink-0">
                                  <span className="text-base font-bold text-text-muted-dark">
                                    {product.name.substring(0, 2).toUpperCase()}
                                  </span>
                                </div>
                              )}
                              <div>
                                <div className="font-medium">{product.name}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-0.5 text-xs rounded ${
                              product.type === 'logger' 
                                ? 'bg-green-700/20 text-green-300' 
                                : 'bg-blue-700/20 text-blue-300'
                            }`}>
                              {product.type === 'logger' ? 'Logger' : 'SaaS'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {product.tags?.productModule ? (
                              <span className="text-xs px-2 py-0.5 bg-primary-700/20 text-primary-300 rounded">
                                {product.tags.productModule}
                              </span>
                            ) : (
                              <span className="text-xs text-text-muted-dark">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm">
                              {product.integration_fields.length} field{product.integration_fields.length !== 1 ? 's' : ''}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="text-sm font-semibold rounded-full bg-green-600/20 px-2.5 py-0.5 text-green-400 border border-green-600/30 inline-block">
                              {integrationCount}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleProductExpand(productId);
                              }}
                              className="premium-button-outline py-1 px-3 text-xs text-blue-400 hover:text-blue-300 hover:border-blue-500/50"
                            >
                              <span className="flex items-center">
                                View Customers
                                <ChevronRight size={14} className="ml-1" />
                              </span>
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center">
                        <div className="flex flex-col items-center">
                          <Boxes size={24} className="mb-2 text-gray-500" />
                          <span className="text-text-muted-dark">No products match the current filters</span>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )
      ) : activeView === 'logger' ? (
        clientViewMode === 'grid' ? (
          renderLoggerGridView()
        ) : (
          renderLoggerTableView()
        )
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 relative z-0">
          <div className="card relative z-10">
            <h2 className="text-lg font-semibold mb-4 flex items-center">
              <BarChart3 size={18} className="mr-2" />
              Integration Stats by Product
            </h2>
            
            <div className="space-y-4 relative z-10">
              {filteredProducts.map(product => {
                const productId = product.id || product._id as string;
                const integrationCount = getProductIntegrationCount(productId);
                
                return (
                  <div key={productId} className="flex items-center relative z-10">
                    <div className="w-28 sm:w-40 truncate mr-3">
                      {product.name}
                    </div>
                    <div className="flex-1 h-6 bg-background-darker rounded-full overflow-hidden relative z-10">
                      <div 
                        className="h-full bg-primary rounded-full flex items-center justify-end px-2 relative z-10"
                        style={{ width: `${integrationCount > 0 ? Math.max(integrationCount, 5) : 0}%` }}
                      >
                        {integrationCount > 15 && (
                          <span className="text-xs text-white relative z-10">
                            {integrationCount}/{displayedOrganizations.length}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          
          <div className="grid grid-cols-1 gap-6">
            {/* Organizations with Most Integrations */}
            <div className="card relative z-10">
              <h2 className="text-lg font-semibold mb-4 flex items-center">
                <Users size={18} className="mr-2" />
                customers with Most Integrations
              </h2>
              
              <div className="space-y-4 relative z-10">
                {displayedOrganizations.slice(0, 10).map(org => {
                  // Count how many products this org has integrated
                  const orgIntegrationCount = products.reduce((count, product) => {
                    const productId = product.id || product._id as string;
                    return count + (integrationStatus[productId]?.[org.id] ? 1 : 0);
                  }, 0);
                  
                  const integrationPercentage = Math.round((orgIntegrationCount / products.length) * 100);
                  
                  // Get level color
                  const levelColors = {
                    'L0': 'bg-blue-100 text-blue-800',
                    'L2': 'bg-purple-100 text-purple-800',
                    'L3': 'bg-green-100 text-green-800',
                  };
                  
                  const levelColor = levelColors[org.level as keyof typeof levelColors] || 'bg-gray-100 text-gray-800';
                  
                  return (
                    <div key={org.id} className="flex items-center relative z-10">
                      <div className="w-28 sm:w-40 truncate mr-3 flex items-center gap-1">
                        <span>{org.name}</span>
                        <span className={`text-xs px-1 py-0.5 rounded ${levelColor}`}>
                          {org.level}
                        </span>
                      </div>
                      <div className="flex-1 h-6 bg-background-darker rounded-full overflow-hidden relative z-10">
                        <div 
                          className="h-full bg-secondary rounded-full flex items-center justify-end px-2 relative z-10"
                          style={{ width: `${orgIntegrationCount > 0 ? Math.max((orgIntegrationCount / products.length) * 100, 5) : 0}%` }}
                        >
                          {orgIntegrationCount > 3 && (
                            <span className="text-xs text-white relative z-10">
                              {orgIntegrationCount}/{products.length}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                
                {displayedOrganizations.length === 0 && (
                  <div className="text-center text-text-muted-dark py-4">
                    No customers match the current filters
                  </div>
                )}
              </div>
            </div>
            
            {/* Integration breakdown by organization level */}
            <div className="card relative z-10">
              <h2 className="text-lg font-semibold mb-4 flex items-center">
                <Layers size={18} className="mr-2" />
                Integration by Organization Level
              </h2>
              
              <div className="space-y-6 relative z-10">
                {availableLevels.filter(level => selectedLevels.includes(level)).map(level => {
                  // Get organizations for this level
                  const orgsForLevel = organizations.filter(org => org.level === level);
                  
                  if (orgsForLevel.length === 0) {
                    return null;
                  }
                  
                  // Count total integrations for this level
                  let totalIntegrations = 0;
                  let possibleIntegrations = orgsForLevel.length * products.length;
                  
                  products.forEach(product => {
                    const productId = product.id || product._id as string;
                    
                    orgsForLevel.forEach(org => {
                      if (integrationStatus[productId]?.[org.id]) {
                        totalIntegrations++;
                      }
                    });
                  });
                  
                  const integrationPercentage = possibleIntegrations > 0 
                    ? Math.round((totalIntegrations / possibleIntegrations) * 100)
                    : 0;
                  
                  // Get level color
                  const levelColors = {
                    'L0': 'bg-blue-600',
                    'L2': 'bg-purple-600',
                    'L3': 'bg-green-600',
                  };
                  
                  const barColor = levelColors[level as keyof typeof levelColors] || 'bg-gray-600';
                  
                  return (
                    <div key={level} className="relative z-10">
                      <div className="flex items-center justify-between mb-1">
                        <div className="font-medium flex items-center">
                          <span className={`inline-block w-3 h-3 rounded-full mr-2 ${barColor}`}></span>
                          {level} Customers ({orgsForLevel.length})
                        </div>
                        <div className="text-sm">
                          {totalIntegrations} / {possibleIntegrations} integrations
                        </div>
                      </div>
                      
                      <div className="h-6 bg-background-darker rounded-full overflow-hidden relative z-10">
                        <div 
                          className={`h-full ${barColor} rounded-full flex items-center justify-center relative z-10`}
                          style={{ width: `${integrationPercentage > 0 ? Math.max(integrationPercentage, 5) : 0}%` }}
                        >
                          {integrationPercentage > 15 && (
                            <span className="text-xs text-white font-medium relative z-10">
                              {integrationPercentage}%
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {integrationPercentage <= 15 && (
                        <div className="text-xs mt-1 text-text-muted-dark">
                          {integrationPercentage}%
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Product Detail Modal - Moved outside of product card mapping to prevent stacking/overlap issues */}
      {expandedProductId && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4 overflow-hidden" 
          onClick={() => setExpandedProductId(null)}
        >
          {products.filter(p => (p.id || p._id as string) === expandedProductId).map(product => {
            const productId = product.id || product._id as string;
            
            return (
              <div 
                key={`modal-${productId}`}
                className="bg-background-card rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-y-auto animate-modalOpen"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Product header */}
                <div className="flex items-center justify-between p-4 border-b border-border-dark sticky top-0 bg-background-card z-10">
                  <div className="flex items-center">
                    {product.logo_url ? (
                      <img 
                        src={product.logo_url}
                        alt={product.name}
                        className="h-12 w-12 mr-3 object-contain bg-white rounded p-1"
                      />
                    ) : (
                      <div className="h-12 w-12 mr-3 flex items-center justify-center bg-background-hover rounded">
                        <span className="text-lg font-bold text-text-muted-dark">
                          {product.name.substring(0, 2).toUpperCase()}
                        </span>
                      </div>
                    )}
                <div>
                      <h2 className="text-xl font-semibold">{product.name} <span className="text-sm text-primary-300 ml-2 bg-primary-700/20 px-2 py-0.5 rounded-full">{getProductIntegrationCount(productId)} integrations</span></h2>
                      <div className="flex items-center text-sm text-text-muted-dark">
                        {product.tags?.productModule ? product.tags.productModule : ''}
                        <span className="mx-2">•</span>
                        {product.integration_fields.length} integration field{product.integration_fields.length !== 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>
                  <button 
                    onClick={() => setExpandedProductId(null)}
                    className="p-2 rounded-full hover:bg-background-hover text-gray-400 hover:text-gray-200 transition-colors"
                    aria-label="Close"
                  >
                    <X size={24} />
                  </button>
                </div>

                {/* Client Organizations heading */}
                <div className="p-4 border-b border-border-dark">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium flex items-center">
                      <Users size={18} className="mr-2" />
                      Client Customers
                    </h3>
                    
                    <div className="flex items-center space-x-3">
                      {/* View mode toggle */}
                      <div className="premium-tabs">
                        <button 
                          onClick={() => toggleClientViewMode('grid')}
                          className={`px-3 py-1.5 flex items-center ${clientViewMode === 'grid' ? 'premium-tab-active' : ''}`}
                          aria-label="Grid view"
                          title="Grid View"
                        >
                          <LayoutGrid size={18} />
                        </button>
                        <button 
                          onClick={() => toggleClientViewMode('table')}
                          className={`px-3 py-1.5 flex items-center ${clientViewMode === 'table' ? 'premium-tab-active' : ''}`}
                          aria-label="Table view"
                          title="Table View"
                        >
                          <List size={18} />
                        </button>
                      </div>
                      
                      {loadingAdditionalData && !dataFullyLoaded && (
                        <ContentLoader
                          message="Loading data..."
                          size="sm"
                          variant="inline"
                        />
                      )}
                    </div>
                  </div>
                </div>

                {/* Client Organizations grid */}
                <div className="p-4">
                  {/* Filter Controls for organizations */}
                  <div className="mb-6 bg-background-card border border-card-border rounded-lg p-4">
                    <div className="flex flex-col md:flex-row gap-4">
                      {/* Search Bar */}
                      <div className="relative flex-grow">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Search size={18} className="text-gray-400" />
                        </div>
                        <input
                          type="text"
                          className="w-full pl-10 pr-4 py-2 bg-background-hover border border-border-dark rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                          placeholder="Search customers by name..."
                          value={searchTerm}
                          onChange={handleSearchChange}
                        />
                        {searchTerm && (
                          <button
                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                            onClick={() => setSearchTerm('')}
                          >
                            <X size={16} className="text-gray-400 hover:text-white transition-colors" />
                          </button>
                        )}
                      </div>

                      {/* Filter Toggle Button */}
                      <button
                        onClick={() => setShowFilters(!showFilters)}
                        className={`flex items-center px-4 py-2 rounded-lg border transition-all ${
                          showFilters 
                            ? 'bg-primary/20 border-primary/40 text-primary-300' 
                            : 'border-border-dark bg-background-hover text-gray-300 hover:bg-background-darker'
                        }`}
                      >
                        <SlidersHorizontal size={16} className="mr-2" />
                        Filters
                      </button>
                    </div>

                    {/* Expanded Filter Options */}
                    {showFilters && (
                      <div className="mt-4 pt-4 border-t border-border-dark animate-fadeIn">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Organization Level Dropdown */}
                          <div>
                            <div className="flex items-center text-gray-400 mb-2">
                              <Users size={16} className="mr-1" />
                              <span className="text-sm">Organization Level:</span>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {availableLevels.map(level => (
                                <button
                                  key={level}
                                  onClick={() => {
                                    setSelectedLevels([level]);
                                    // Clear selected orgs to show all of this level
                                    setSelectedOrgs(organizations.filter(org => org.level === level));
                                  }}
                                  className={`px-4 py-1.5 text-sm rounded-full transition-all flex items-center ${
                                    selectedLevels.includes(level) && selectedLevels.length === 1
                                      ? level === 'L0' ? 'bg-blue-600 text-white shadow-md shadow-blue-500/20' 
                                      : level === 'L2' ? 'bg-purple-600 text-white shadow-md shadow-purple-500/20' 
                                      : 'bg-green-600 text-white shadow-md shadow-green-500/20'
                                      : 'bg-background-hover text-text-muted hover:bg-background-darker border border-border-dark'
                                  }`}
                                >
                                  {level}
                                  <span className={`ml-2 text-xs py-0.5 px-2 rounded-full ${
                                    selectedLevels.includes(level) && selectedLevels.length === 1
                                      ? 'bg-white/20 text-white'
                                      : level === 'L0' ? 'bg-blue-700/20 text-blue-300'
                                      : level === 'L2' ? 'bg-purple-700/20 text-purple-300'
                                      : 'bg-green-700/20 text-green-300'
                                  }`}>
                                    {organizations.filter(org => org.level === level).length}
                                  </span>
                                </button>
                              ))}
                              <button
                                onClick={() => {
                                  setSelectedLevels(['L0', 'L2', 'L3']);
                                  // Show all organizations
                                  setSelectedOrgs(organizations);
                                }}
                                className={`px-4 py-1.5 text-sm rounded-full transition-all flex items-center ${
                                  selectedLevels.length === 3
                                    ? 'bg-primary text-white shadow-md shadow-primary/20'
                                    : 'bg-background-hover text-text-muted hover:bg-background-darker border border-border-dark'
                                }`}
                              >
                                All Levels
                                <span className={`ml-2 text-xs py-0.5 px-2 rounded-full ${
                                  selectedLevels.length === 3
                                    ? 'bg-white/20 text-white'
                                    : 'bg-gray-700/20 text-gray-300'
                                }`}>
                                  {organizations.length}
                                </span>
                              </button>
                            </div>
                          </div>
                          
                          {/* Client Selection Dropdown */}
                          <div>
                            <div className="flex items-center text-gray-400 mb-2">
                              <Building2 size={16} className="mr-1" />
                              <span className="text-sm">Client Selection:</span>
                            </div>
                            <select 
                              className="w-full bg-background-hover border border-border-dark rounded-lg px-3 py-2.5 text-sm text-gray-200 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                              onChange={(e) => {
                                const orgId = e.target.value;
                                if (orgId === 'all') {
                                  // Show all clients that match the selected level
                                  setSelectedOrgs(organizations.filter(org => 
                                    selectedLevels.includes(org.level)
                                  ));
                                } else if (orgId === 'integrated') {
                                  // Show only integrated clients of the selected level
                                  const integratedOrgs = organizations.filter(org => 
                                    selectedLevels.includes(org.level) && 
                                    integrationStatus[productId]?.[org.id] === true
                                  );
                                  
                                  if (integratedOrgs.length === 0) {
                                    // If no integrated orgs found, show message and display all orgs of selected level
                                    toast(`No integrated clients found for this product. Showing all clients.`, {
                                      icon: 'ℹ️',
                                      duration: 3000
                                    });
                                    setSelectedOrgs(organizations.filter(org => 
                                      selectedLevels.includes(org.level)
                                    ));
                                  } else {
                                    setSelectedOrgs(integratedOrgs);
                                  }
                                } else if (orgId === 'not-integrated') {
                                  // Show only non-integrated clients of the selected level
                                  const nonIntegratedOrgs = organizations.filter(org => 
                                    selectedLevels.includes(org.level) && 
                                    !integrationStatus[productId]?.[org.id]
                                  );
                                  
                                  if (nonIntegratedOrgs.length === 0) {
                                    toast(`All clients are already integrated. Showing all clients.`, {
                                      icon: 'ℹ️',
                                      duration: 3000
                                    });
                                    setSelectedOrgs(organizations.filter(org => 
                                      selectedLevels.includes(org.level)
                                    ));
                                  } else {
                                    setSelectedOrgs(nonIntegratedOrgs);
                                  }
                                } else {
                                  // Show specific client
                                  const org = organizations.find(o => o.id === orgId);
                                  if (org) {
                                    setSelectedOrgs([org]);
                                  }
                                }
                              }}
                              defaultValue="integrated"
                            >
                              <option value="integrated">Integrated Clients</option>
                              <option value="not-integrated">Not Integrated Clients</option>
                              <option value="all">All Clients</option>
                              {organizations
                                .filter(org => selectedLevels.includes(org.level))
                                .map(org => (
                                  <option key={org.id} value={org.id}>
                                    {org.name} ({org.level})
                                  </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {/* Filter Summary */}
                        <div className="mt-4 flex flex-wrap items-center gap-2">
                          <div className="text-xs text-gray-400">
                            Filters applied:
                          </div>
                          
                          {/* Level filter badge */}
                          {selectedLevels.length < availableLevels.length && (
                            <div className="bg-indigo-700/20 text-indigo-300 text-xs px-2.5 py-1 rounded-full flex items-center">
                              Level: {selectedLevels.join(', ')}
                            </div>
                          )}
                          
                          {/* Integration status badge */}
                          {selectedOrgs.length > 0 && selectedOrgs.every(org => integrationStatus[productId]?.[org.id] === true) && (
                            <div className="bg-green-700/20 text-green-300 text-xs px-2.5 py-1 rounded-full flex items-center">
                              Status: Integrated
                            </div>
                          )}
                          
                          {selectedOrgs.length > 0 && selectedOrgs.every(org => !integrationStatus[productId]?.[org.id]) && (
                            <div className="bg-yellow-700/20 text-yellow-300 text-xs px-2.5 py-1 rounded-full flex items-center">
                              Status: Not Integrated
                            </div>
                          )}
                          
                          {/* Search term badge */}
                          {searchTerm && (
                            <div className="bg-blue-700/20 text-blue-300 text-xs px-2.5 py-1 rounded-full flex items-center">
                              Search: "{searchTerm}"
                            </div>
                          )}
                          
                          {/* Clear Filters Button */}
                          {(selectedLevels.length < availableLevels.length || selectedOrgs.length > 0 || searchTerm) && (
                            <button
                              onClick={() => {
                                setSelectedLevels(['L3']);
                                setSelectedOrgs([]);
                                setSearchTerm('');
                              }}
                              className="ml-auto text-xs text-gray-400 hover:text-white flex items-center border border-border-dark bg-background-hover hover:bg-background-darker px-3 py-1.5 rounded-md transition-colors"
                            >
                              <X size={14} className="mr-1" />
                              Clear Filters
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Results Summary */}
                  <div className="mb-4 flex justify-between items-center">
                    <div className="text-sm text-gray-400">
                      Showing <span className="text-white font-medium">
                        {getFilteredOrganizationsForProduct(productId).length}
                      </span> customers
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <button
                        className={`p-2 rounded-md border ${
                          clientViewMode === 'grid'
                            ? 'bg-primary/20 border-primary/40 text-primary-300'
                            : 'border-border-dark bg-background-hover text-gray-400 hover:text-gray-200'
                        }`}
                        onClick={() => toggleClientViewMode('grid')}
                        title="Grid View"
                        aria-label="Switch to Grid View"
                      >
                        <LayoutGrid size={18} />
                      </button>
                      <button
                        className={`p-2 rounded-md border ${
                          clientViewMode === 'table'
                            ? 'bg-primary/20 border-primary/40 text-primary-300'
                            : 'border-border-dark bg-background-hover text-gray-400 hover:text-gray-200'
                        }`}
                        onClick={() => toggleClientViewMode('table')}
                        title="Table View"
                        aria-label="Switch to Table View"
                      >
                        <List size={18} />
                      </button>
                    </div>
                  </div>
                  
                  {/* Organizations display based on view mode */}
                  {clientViewMode === 'grid' ? (
                    // Grid View
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {displayedOrganizations.length > 0 ? (
                        getFilteredOrganizationsForProduct(productId).map(org => {
                          const isIntegrated = integrationStatus[productId]?.[org.id] || false;
                          const isActive = activationStatus[productId]?.[org.id] || false;
                          const isLoading = !dataFullyLoaded && !integrationStatus[productId]?.[org.id] && 
                            integrationStatus[productId]?.[org.id] !== false;
                          const integrationDate = integrationDates[productId]?.[org.id] || null;
                          
                          // Define colors and badges for different levels
                          const levelBadges = {
                            'L0': { bg: 'bg-blue-600', text: 'L0' },
                            'L2': { bg: 'bg-purple-600', text: 'L2' },
                            'L3': { bg: 'bg-green-600', text: 'L3' }
                          };
                          
                          const levelBadge = levelBadges[org.level as keyof typeof levelBadges] || { bg: 'bg-gray-600', text: org.level };
                          
                          return (
                            <div key={org.id} className="card hover:border-primary/50 transition-colors relative p-4">
                              <div className="flex items-start justify-between mb-3">
                                <div className="flex items-center flex-grow">
                                  <Building2 size={18} className="mr-2 text-text-muted-dark flex-shrink-0" />
                                  <div className="flex-grow">
                                    <div className="flex items-center">
                                      <h3 className="font-medium text-sm">{org.name}</h3>
                                      <span className={`ml-2 px-2 py-0.5 text-xs text-white rounded-md ${levelBadge.bg}`}>
                                        {levelBadge.text}
                                      </span>
                                    </div>
                                    <code className="px-2 py-0.5 rounded text-xs text-white">{org.tenant_code}</code>
                                  </div>
                                </div>
                                
                                {/* Activation toggle positioned at top right */}
                                <div className="ml-2">
                                  <div className="toggle-container">
                                    <span className={`toggle-status ${isActive ? "active" : "inactive"}`}>
                                      {isActive ? "Active" : "Inactive"}
                                    </span>
                                    <label className={`toggle-switch ${loadingToggles[`${productId}_${org.id}`] || !canManageIntegrations ? "opacity-50" : ""}`}>
                                      <input 
                                        type="checkbox" 
                                        checked={isActive}
                                        onChange={() => toggleActivationStatus(productId, org.id, isActive)}
                                        className="sr-only"
                                        disabled={loadingToggles[`${productId}_${org.id}`] || !canManageIntegrations}
                                      />
                                      <div className={`toggle-track ${isActive ? "active" : "inactive"}`}>
                                        {loadingToggles[`${productId}_${org.id}`] ? (
                                          <Loader2 size={14} className="absolute top-1 left-1 animate-spin text-white" />
                                        ) : (
                                          <span className="toggle-thumb"></span>
                                        )}
                                      </div>
                                    </label>
                                  </div>
                                </div>
                              </div>
                              
                              {/* Status Section */}
                              <div className="border-t border-border-dark pt-3 mb-3">
                                <div className="flex items-start">
                                  <div className="text-sm">
                                    <div className="mb-1">
                                      <span className="text-text-muted-dark">Integration Status: </span>
                                      {isLoading ? (
                                        <span className="flex items-center text-text-muted-dark">
                                          <Loader2 size={12} className="animate-spin mr-1" />
                                          Checking...
                                        </span>
                                      ) : isIntegrated ? (
                                        <span className="text-status-success flex items-center">
                                          <CheckCircle size={14} className="mr-1" />
                                          Integrated
                                        </span>
                                      ) : (
                                        <span className="text-status-warning flex items-center">
                                          <AlertTriangle size={14} className="mr-1" />
                                          Not Integrated
                                        </span>
                                      )}
                                    </div>
                                    
                                    {/* Integration date if available */}
                                    {isIntegrated && integrationDate && (
                                      <div className="text-xs text-text-muted-dark flex items-center">
                                        <Calendar size={12} className="mr-1" />
                                        Integrated on: {formatDate(integrationDate)}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                              
                              {/* Action Button */}
                              <div className="border-t border-border-dark pt-3 flex justify-between">
                                {isIntegrated && (
                                  <GridDeleteButton
                                    productId={productId}
                                    orgId={org.id}
                                    orgName={org.name}
                                    isLoading={loadingToggles[`${productId}_${org.id}`]}
                                  />
                                )}
                                <div className="flex space-x-2">
                                  {/* Download button for logger products */}
                                  {product.type === 'logger' && isIntegrated && (
                                    <button 
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDownloadLoggerConfig(product, org);
                                      }}
                                      className="premium-button-outline py-1 px-3 text-xs text-blue-400 hover:text-blue-300 hover:border-blue-500/50"
                                      disabled={loadingToggles[`${productId}_${org.id}`]}
                                    >
                                      <span className="flex items-center">
                                        {loadingToggles[`${productId}_${org.id}`] ? (
                                          <Loader2 size={12} className="animate-spin mr-1" />
                                        ) : (
                                          <Download size={12} className="mr-1" />
                                        )}
                                        Download
                                      </span>
                                    </button>
                                  )}
                                  {canManageIntegrations() && (
                                    <button 
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        // Check product type and open appropriate modal
                                        if (product.type === 'logger') {
                                          openLoggerConfigModal(product, org);
                                        } else {
                                          openConfigModal(product, org);
                                        }
                                      }}
                                      className={`premium-button-outline py-1 px-3 text-xs ${isIntegrated ? 'text-blue-400 hover:text-blue-300 hover:border-blue-500/50' : 'text-primary-400 hover:text-primary-300 hover:border-primary-500/50'}`}
                                    >
                                      <span className="flex items-center">
                                        {isIntegrated ? 'Update' : 'Configure'}
                                        <ExternalLink size={12} className="ml-1" />
                                      </span>
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="text-center text-text-muted-dark py-6 px-4">
                          <div className="flex flex-col items-center">
                            <Building2 size={24} className="mb-2 text-gray-500" />
                            No customers match the current filters
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    // Table View - Complete replacement with more robust implementation
                    <div className="rounded-lg border border-border-dark overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="bg-background-hover border-b border-border-dark">
                              <th className="px-4 py-3 text-left font-medium text-sm">Organization</th>
                              <th className="px-4 py-3 text-left font-medium text-sm">Level</th>
                              <th className="px-4 py-3 text-left font-medium text-sm">Integration Status</th>
                              <th className="px-4 py-3 text-left font-medium text-sm">Integration Date</th>
                              <th className="px-4 py-3 text-center font-medium text-sm">Activation</th>
                              <th className="px-4 py-3 text-right font-medium text-sm">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {getFilteredOrganizationsForProduct(productId).length > 0 ? (
                              getFilteredOrganizationsForProduct(productId).map(org => {
                                const isIntegrated = integrationStatus[productId]?.[org.id] || false;
                                const isActive = activationStatus[productId]?.[org.id] || false;
                                const isLoading = !dataFullyLoaded && !integrationStatus[productId]?.[org.id] && 
                                  integrationStatus[productId]?.[org.id] !== false;
                                const integrationDate = integrationDates[productId]?.[org.id] || null;
                                
                                // Define colors and badges for different levels
                                const levelBadges = {
                                  'L0': { bg: 'bg-blue-600', text: 'L0' },
                                  'L2': { bg: 'bg-purple-600', text: 'L2' },
                                  'L3': { bg: 'bg-green-600', text: 'L3' }
                                };
                                
                                const levelBadge = levelBadges[org.level as keyof typeof levelBadges] || { bg: 'bg-gray-600', text: org.level };
                                
                                return (
                                  <tr key={org.id} className={`border-t border-border-dark ${isIntegrated ? 'bg-primary/5' : ''}`}>
                                    <td className="px-4 py-3">
                                      <div className="flex items-center">
                                        <Building2 size={16} className="mr-2 text-text-muted-dark flex-shrink-0" />
                                        <div>
                                          <div className="font-medium text-sm truncate max-w-[180px]">{org.name}</div>
                                          <code className="text-xs bg-background-darker px-1.5 py-0.5 rounded mt-0.5 inline-block">{org.tenant_code}</code>
                                        </div>
                                      </div>
                                    </td>
                                    <td className="px-4 py-3">
                                      <span className={`px-2 py-0.5 text-xs text-white rounded-md ${levelBadge.bg}`}>
                                        {levelBadge.text}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3">
                                      {isLoading ? (
                                        <span className="flex items-center text-text-muted-dark">
                                          <Loader2 size={12} className="animate-spin mr-1" />
                                          Checking...
                                        </span>
                                      ) : isIntegrated ? (
                                        <span className="text-status-success flex items-center">
                                          <CheckCircle size={14} className="mr-1" />
                                          Integrated
                                        </span>
                                      ) : (
                                        <span className="text-status-warning flex items-center">
                                          <AlertTriangle size={14} className="mr-1" />
                                          Not Integrated
                                        </span>
                                      )}
                                    </td>
                                    <td className="px-4 py-3">
                                      {isIntegrated && integrationDate ? (
                                        <span className="text-xs text-text-muted-dark flex items-center">
                                          <Calendar size={12} className="mr-1" />
                                          {formatDate(integrationDate)}
                                        </span>
                                      ) : (
                                        <span className="text-xs text-text-muted-dark">-</span>
                                      )}
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                      <div className="flex items-center justify-center">
                                        <div className="toggle-container">
                                          <label className={`toggle-switch ${loadingToggles[`${productId}_${org.id}`] || !canManageIntegrations ? "opacity-50" : ""}`}>
                                            <input 
                                              type="checkbox" 
                                              checked={isActive}
                                              onChange={() => toggleActivationStatus(productId, org.id, isActive)}
                                              className="sr-only"
                                              disabled={loadingToggles[`${productId}_${org.id}`] || !canManageIntegrations}
                                            />
                                            <div className={`toggle-track ${isActive ? "active" : "inactive"}`}>
                                              {loadingToggles[`${productId}_${org.id}`] ? (
                                                <Loader2 size={14} className="absolute top-1 left-1 animate-spin text-white" />
                                              ) : (
                                                <span className="toggle-thumb"></span>
                                              )}
                                            </div>
                                          </label>
                                          <span className={`toggle-status ${isActive ? "active" : "inactive"} ml-2`}>
                                            {isActive ? "Active" : "Inactive"}
                                          </span>
                                        </div>
                                      </div>
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                      <div className="flex justify-end space-x-2">
                                        {isIntegrated && (
                                          <TableDeleteButton
                                            productId={productId}
                                            orgId={org.id}
                                            orgName={org.name}
                                            isLoading={loadingToggles[`${productId}_${org.id}`]}
                                          />
                                        )}
                                        <div className="flex space-x-2">
                                          {/* Download button for logger products */}
                                          {product.type === 'logger' && isIntegrated && (
                                            <button 
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleDownloadLoggerConfig(product, org);
                                              }}
                                              className="premium-button-outline py-1 px-3 text-xs text-blue-400 hover:text-blue-300 hover:border-blue-500/50"
                                              disabled={loadingToggles[`${productId}_${org.id}`]}
                                            >
                                              <span className="flex items-center">
                                                {loadingToggles[`${productId}_${org.id}`] ? (
                                                  <Loader2 size={12} className="animate-spin mr-1" />
                                                ) : (
                                                  <Download size={12} className="mr-1" />
                                                )}
                                                Download
                                              </span>
                                            </button>
                                          )}
                                          {canManageIntegrations() && (
                                            <button 
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                // Check product type and open appropriate modal
                                                if (product.type === 'logger') {
                                                  openLoggerConfigModal(product, org);
                                                } else {
                                                  openConfigModal(product, org);
                                                }
                                              }}
                                              className={`premium-button-outline py-1 px-3 text-xs ${isIntegrated ? 'text-blue-400 hover:text-blue-300 hover:border-blue-500/50' : 'text-primary-400 hover:text-primary-300 hover:border-primary-500/50'}`}
                                            >
                                              <span className="flex items-center">
                                                {isIntegrated ? 'Update' : 'Configure'}
                                                <ExternalLink size={12} className="ml-1" />
                                              </span>
                                            </button>
                                          )}
                                        </div>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })
                            ) : (
                              <tr>
                                <td colSpan={6} className="px-4 py-8 text-center">
                                  <div className="flex flex-col items-center">
                                    <Building2 size={24} className="mb-2 text-gray-500" />
                                    <span className="text-text-muted-dark">No customers match the current filters</span>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  
                  {!dataFullyLoaded && loadingAdditionalData && displayedOrganizations.length === 0 && (
                    <div className="py-6">
                      <ContentLoader
                        message="Loading organization data..."
                        size="lg"
                        withCard={false}
                        minHeight="200px"
                      />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      
      {/* Integration Config Modal */}
      {configModalOpen && selectedProductForConfig && selectedOrgForConfig && (
        <IntegrationConfigModal
          product={selectedProductForConfig}
          organization={selectedOrgForConfig}
          isOpen={configModalOpen}
          onClose={() => setConfigModalOpen(false)}
          onSave={handleSaveConfig}
        />
      )}
      
      {/* Logger Config Modal */}
      {loggerConfigModalOpen && selectedProductForLoggerConfig && selectedOrgForLoggerConfig && (
        <LoggerConfigModal
          product={selectedProductForLoggerConfig}
          organization={selectedOrgForLoggerConfig}
          isOpen={loggerConfigModalOpen}
          onClose={() => setLoggerConfigModalOpen(false)}
          onSaved={fetchData}
        />
      )}
      
      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setDeleteInfo(null);
        }}
        onConfirm={confirmDeleteIntegration}
        title="Delete Integration"
        message={deleteInfo ? `Are you sure you want to delete the integration for ${deleteInfo.orgName}? This action cannot be undone.` : ''}
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
        isDanger={true}
      />
      
      {/* View Config Modal */}
      <ViewConfigModal
        isOpen={viewConfigModalOpen}
        onClose={() => setViewConfigModalOpen(false)}
        configContent={configContent}
        orgName={configOrgName}
      />
      
      {/* Download Configuration Modal */}
      <DownloadConfigModal
        isOpen={downloadModalOpen}
        onClose={() => setDownloadModalOpen(false)}
        products={products
          .filter(p => {
            const productId = p.id || p._id as string;
            return p.type === 'logger' && selectedOrgForDownload && integrationStatus[productId]?.[selectedOrgForDownload.id];
          })
          .map(mapToLoggerProduct) as LoggerProduct[]}
        selectedProducts={productsForDownload}
        onProductToggle={handleProductToggleForDownload}
        onDownload={handleDownloadWithSelectedProducts}
        isDownloading={isDownloading}
      />
    </div>
  );
};

export default IntegrationsPage; 